import { smallBusinessWord } from "../../../model/steuerbegriffe";
import { convertTime, deliverableLabel, summarizeDeliverables, type DeliverableLike } from "@talentkontor/domain";
import { PLATFORM } from "../../agentur/uebersicht/labels";
import type { Pill, Tone } from "../../../model/invoice-view";
import { date, money, monthLabel, monthName, time, numberLocale } from "../../../lib/format";
import { dateOf, monthOf, type Lang } from "../../creator/lang";
import { currentLang, t, ta } from "../../../lib/sprache";
import { oeffenbar } from "../../../lib/sichere-datei";

/**
 * Anzeige-Logik für Nachrichten (Chat, 0014) und Benachrichtigungen (Glocke, 0011/0017/0021) als reine Funktionen.
 * Keine Datenbankzugriffe: die Lader stehen in data.ts, die Seiten setzen nur zusammen. Rechte prüft die Datenbank.
 */

export type Portal = "agentur" | "creator";
export type Side = "agency" | "talent";
export type LinkKind = "invoice" | "contract" | "deal";

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
export const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

/** Die sechs Reaktionen, die die Datenbank annimmt (message_reactions.emoji). */
export const EMOJIS = ["👍", "❤️", "😂", "😮", "🙏", "✅"] as const;
/** Längste Nachricht (Prüfung in messages.body). */
export const MAX_BODY = 4000;
/** So viele Nachrichten lädt ein Verlauf zuerst; ältere auf Knopfdruck. */
export const PAGE_SIZE = 100;

// ── Zeilen aus der Datenbank ────────────────────────────────────────────────

/** Sicht my_chat_threads: je sichtbarem Creator ein Faden, auch ohne Nachrichten. */
export interface ThreadRow {
  talent_id: string;
  org_id: string;
  my_side: Side | null;
  last_at: string | null;
  last_body: string | null;
  last_side: Side | null;
  unread: number;
}
export interface TalentLite {
  id: string;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  status: string;
}
export interface MessageRow {
  id: string;
  org_id: string;
  talent_id: string;
  sender_user_id: string | null;
  sender_side: Side;
  body: string;
  link_kind: LinkKind | null;
  link_id: string | null;
  created_at: string;
  read_at: string | null;
  /** zurückgezogen (0041): Text, Verknüpfung und Dateien sind entfernt */
  retracted_at?: string | null;
  /** Teil einer Rundnachricht an mehrere Creator (0056) */
  broadcast_id?: string | null;
  /** Antwort mit Zitat (0393): Nachricht desselben Fadens */
  reply_to?: string | null;
}
/** Interne Notiz des Teams im Faden (0393, Tabelle message_notes): der Creator sieht sie nie. */
export interface NoteRow {
  id: string;
  org_id: string;
  talent_id: string;
  author_user_id: string | null;
  body: string;
  mentions: string[];
  created_at: string;
}
/** So lange lassen sich eigene Nachrichten zurückziehen (platform_defaults.chat_retract_hours, 0041). */
export const RETRACT_HOURS = 24;
/** Eigene Nachricht, noch nicht zurückgezogen, innerhalb der Frist. */
export function canRetract(m: Pick<MessageRow, "sender_user_id" | "created_at" | "retracted_at">, me: string | null, now: number = Date.now()): boolean {
  return !!me && m.sender_user_id === me && !m.retracted_at && now - Date.parse(m.created_at) < RETRACT_HOURS * 3_600_000;
}
export interface ReactionRow {
  message_id: string;
  user_id: string;
  emoji: string;
  created_at?: string;
}
export interface AttachmentRow {
  id: string;
  owner_id: string;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  uploaded_by: string | null;
}
export interface MemberLite {
  user_id: string;
  display_name: string | null;
}

export function personName(t: Pick<TalentLite, "display_name" | "first_name" | "last_name"> | undefined | null): string {
  if (!t) return "Creator";
  return (t.display_name ?? `${t.first_name} ${t.last_name ?? ""}`).trim() || "Creator";
}

// ── Zeit und Tage (in der Zeitzone der Agentur) ─────────────────────────────

/** Kalendertag eines Zeitpunkts als 'YYYY-MM-DD' in einer Zeitzone. */
export function localDay(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
/** Ganze Tage von `from` bis `to` (beide 'YYYY-MM-DD'); positiv, wenn `to` später liegt. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}
/**
 * „Heute“, „Gestern“ oder „22. September 2026“ (Gruppen der Glocke); englisch „Today“ … Mit `ohneJahr` entfällt das
 * Jahr, wenn der Tag im laufenden Jahr liegt (Tagestrenner im Verlauf wie im Messenger: „14. Oktober“).
 */
export function dayLabel(day: string, today: string, lang: Lang = currentLang(), opts: { ohneJahr?: boolean } = {}): string {
  if (day === today) return lang === "en" ? "Today" : "Heute";
  if (day === addDays(today, -1)) return lang === "en" ? "Yesterday" : "Gestern";
  const [y, , d] = day.split("-");
  const jahr = opts.ohneJahr && y === today.slice(0, 4) ? "" : ` ${y}`;
  return lang === "en" ? `${Number(d)} ${monthOf(day, "en", false)}${jahr}` : `${Number(d)}. ${monthName(day)}${jahr}`;
}
/** Kurzform für Listen: „10:24“ (heute), „Gestern“, „22.09.“ (dieses Jahr) oder „22.09.2025“. */
export function shortWhen(iso: string, today: string, timeZone: string, lang: Lang = currentLang()): string {
  const day = localDay(iso, timeZone);
  if (day === today) return time(iso, timeZone, lang === "en" ? "en-GB" : "de-DE");
  if (day === addDays(today, -1)) return lang === "en" ? "Yesterday" : "Gestern";
  const [y, m, d] = day.split("-");
  const sep = lang === "en" ? "/" : ".";
  return y === today.slice(0, 4) ? `${d}${sep}${m}${lang === "en" ? "" : "."}` : `${d}${sep}${m}${sep}${y}`;
}
/** „Heute 10:24“, „Gestern 09:10“ oder „22.09.2026“ (Aufgaben ohne Tagesgruppe). */
export function whenWithDay(iso: string, today: string, timeZone: string, lang: Lang = currentLang()): string {
  const day = localDay(iso, timeZone);
  const t = time(iso, timeZone, lang === "en" ? "en-GB" : "de-DE");
  if (day === today) return lang === "en" ? `Today ${t}` : `Heute ${t}`;
  if (day === addDays(today, -1)) return lang === "en" ? `Yesterday ${t}` : `Gestern ${t}`;
  return lang === "en" ? dateOf(iso, "en", timeZone) : date(iso, timeZone);
}

// ── Gesprächsliste ──────────────────────────────────────────────────────────

export interface ThreadLine {
  talentId: string;
  name: string;
  archived: boolean;
  lastAt: string | null;
  lastSide: Side | null;
  preview: string;
  unread: number;
}

/** Text der letzten Nachricht, auf eine Zeile gekürzt; eigene Nachrichten mit „Du: “. */
export function previewText(body: string | null, lastSide: Side | null, mySide: Side, max = 90, lang: Lang = currentLang()): string {
  if (lastSide === null) return lang === "en" ? "No messages yet" : "Noch keine Nachrichten";
  const clean = (body ?? "").replace(/\s+/g, " ").trim() || (lang === "en" ? "Link shared" : "Verknüpfung geteilt");
  const short = clean.length > max ? `${clean.slice(0, max - 1).trimEnd()} …` : clean;
  return lastSide === mySide ? `${lang === "en" ? "You" : "Du"}: ${short}` : short;
}

/** Fäden der eigenen Seite mit Namen, neueste zuerst; Creator ohne Nachrichten am Ende nach Namen. */
export function threadLines(threads: readonly ThreadRow[], talents: readonly TalentLite[], mySide: Side): ThreadLine[] {
  const byId = new Map(talents.map((t) => [t.id, t]));
  return threads
    .filter((t) => t.my_side === mySide)
    .map((t) => {
      const talent = byId.get(t.talent_id);
      return {
        talentId: t.talent_id,
        name: personName(talent),
        archived: talent?.status === "archived",
        lastAt: t.last_at,
        lastSide: t.last_side,
        preview: previewText(t.last_body, t.last_side, mySide),
        unread: Number(t.unread) || 0,
      };
    })
    .sort((a, b) => {
      if (a.lastAt && b.lastAt) return b.lastAt.localeCompare(a.lastAt) || a.name.localeCompare(b.name, "de");
      if (a.lastAt) return -1;
      if (b.lastAt) return 1;
      return a.name.localeCompare(b.name, "de");
    });
}

export type ThreadFilter = "alle" | "ungelesen";

/**
 * Ohne Suche zeigt die Liste nur Fäden mit Nachrichten (plus den geöffneten). Mit Suche findet sie jeden sichtbaren
 * Creator, damit man eine neue Unterhaltung beginnen kann (AD-180: „Talent suchen, um zu schreiben“).
 */
export function visibleThreadLines(lines: readonly ThreadLine[], search: string, filter: ThreadFilter, selected: string | null): ThreadLine[] {
  const q = search.trim().toLowerCase();
  return lines.filter((l) => {
    if (filter === "ungelesen" && l.unread === 0) return false;
    if (q) return `${l.name} ${l.lastAt ? l.preview : ""}`.toLowerCase().includes(q);
    return l.lastAt !== null || l.talentId === selected;
  });
}

// ── Verlauf ─────────────────────────────────────────────────────────────────

export type ThreadItem =
  | { kind: "day"; key: string; label: string }
  | { kind: "new"; key: string }
  | { kind: "message"; key: string; message: MessageRow; mine: boolean }
  | { kind: "note"; key: string; note: NoteRow; mine: boolean };

/** Erste ungelesene Nachricht der Gegenseite (dort steht der Trenner „Neue Nachrichten“). */
export function firstUnreadIncoming(messages: readonly MessageRow[], mySide: Side): string | null {
  return messages.find((m) => m.sender_side !== mySide && !m.read_at)?.id ?? null;
}
/**
 * Soll der Verlauf jetzt als gelesen gelten? Nur bei sichtbarem Tab und einmal je neuester ungelesener Nachricht
 * (AD-181, TA-115). Ein im Hintergrund verpasstes Markieren holt der Wechsel auf „sichtbar“ nach.
 */
export function shouldMarkRead(lastUnread: string | null, alreadyMarked: string | null, visible: boolean): boolean {
  return !!lastUnread && lastUnread !== alreadyMarked && visible;
}
export function unreadIncoming(messages: readonly MessageRow[], mySide: Side): number {
  return messages.filter((m) => m.sender_side !== mySide && !m.read_at).length;
}

/**
 * Nachrichten in zeitlicher Reihenfolge mit Tagestrennern und dem Trenner vor der ersten neuen Nachricht. Interne Notizen
 * (nur Agentur, 0393) stehen zeitlich dazwischen; sie zählen nicht als neu oder ungelesen.
 */
export function threadItems(messages: readonly MessageRow[], opts: { mySide: Side; today: string; timeZone: string; newFrom: string | null; lang?: Lang;
  notes?: readonly NoteRow[]; me?: string | null }): ThreadItem[] {
  type Entry = { at: string; message?: MessageRow; note?: NoteRow };
  const entries: Entry[] = [...messages.map((m) => ({ at: m.created_at, message: m })), ...(opts.notes ?? []).map((n) => ({ at: n.created_at, note: n }))];
  // Gleicher Zeitpunkt: Nachricht vor Notiz (die Notiz kommentiert meist, was gerade kam); sonst stabile Reihenfolge
  entries.sort((a, b) => a.at.localeCompare(b.at) || (a.message ? 0 : 1) - (b.message ? 0 : 1));
  const out: ThreadItem[] = [];
  let lastDay: string | null = null;
  for (const e of entries) {
    const day = localDay(e.at, opts.timeZone);
    if (day !== lastDay) {
      out.push({ kind: "day", key: `day:${day}`, label: dayLabel(day, opts.today, opts.lang ?? currentLang(), { ohneJahr: true }) });
      lastDay = day;
    }
    if (e.note) {
      out.push({ kind: "note", key: `note:${e.note.id}`, note: e.note, mine: !!opts.me && e.note.author_user_id === opts.me });
      continue;
    }
    const m = e.message!;
    if (opts.newFrom && m.id === opts.newFrom) out.push({ kind: "new", key: "new" });
    out.push({ kind: "message", key: m.id, message: m, mine: m.sender_side === opts.mySide });
  }
  return out;
}

// ── Messenger-Darstellung (D-1 S6) ──────────────────────────────────────────

/** Folgenachrichten desselben Absenders bilden eine Gruppe, solange höchstens so viele Minuten dazwischen liegen. */
export const GRUPPE_MINUTEN = 10;
export interface GruppenLage {
  /** erste Nachricht der Gruppe (Absendername, normaler Abstand nach oben) */
  anfang: boolean;
  /** letzte Nachricht der Gruppe (Ecke an der Absenderseite) */
  ende: boolean;
}
/**
 * Lage jeder Nachricht in ihrer Gruppe: gleiche Seite und gleiche Person, kein Tagestrenner, kein „Neue Nachrichten“ und
 * keine interne Notiz dazwischen, höchstens GRUPPE_MINUTEN Abstand. Schlüssel wie in threadItems.
 */
export function gruppenLage(items: readonly ThreadItem[]): Map<string, GruppenLage> {
  const out = new Map<string, GruppenLage>();
  const gleich = (a: ThreadItem | undefined, b: ThreadItem | undefined): boolean => {
    if (a?.kind !== "message" || b?.kind !== "message") return false;
    const x = a.message;
    const y = b.message;
    return x.sender_side === y.sender_side && (x.sender_user_id ?? "") === (y.sender_user_id ?? "")
      && Math.abs(Date.parse(y.created_at) - Date.parse(x.created_at)) <= GRUPPE_MINUTEN * 60_000;
  };
  items.forEach((it, i) => {
    if (it.kind !== "message") return;
    out.set(it.key, { anfang: !gleich(items[i - 1], it), ende: !gleich(it, items[i + 1]) });
  });
  return out;
}

export type Zustellung = "read" | "delivered" | "mail" | "none";
/** Zustand eigener Nachrichten als Zeichen in der Blase (✓ zugestellt, ✓✓ gelesen, ✉ nur per Mail, ! nicht erreichbar). */
export function zustellung(m: Pick<MessageRow, "read_at">, reach?: "portal" | "mail" | "none"): Zustellung {
  if (m.read_at) return "read";
  if (reach === "mail") return "mail";
  if (reach === "none") return "none";
  return "delivered";
}
export const ZUSTELL_ZEICHEN: Record<Zustellung, string> = { read: "✓✓", delivered: "✓", mail: "✉", none: "!" };

// ── Suche im Verlauf (K-525) ────────────────────────────────────────────────

/** Für den Vergleich: Kleinschreibung, ohne Akzente, Leerraum zusammengefasst (Umlaute bleiben: „ä“ findet „ä“, nicht „a“). */
export function searchFold(s: string): string {
  return s.normalize("NFD").replace(/(?![̈])[̀-ͯ]/g, "").normalize("NFC").toLowerCase().replace(/\s+/g, " ");
}
/**
 * Treffer der Suche im Verlauf in zeitlicher Reihenfolge: Nachrichten (Text und Dateinamen) und interne Notizen. Zurückgezogene
 * Nachrichten haben keinen Text mehr. Ab zwei Zeichen, damit nicht jede Nachricht mit „a“ aufleuchtet.
 */
export function searchHits(items: readonly ThreadItem[], query: string, fileNames: (messageId: string) => readonly string[] = () => []): string[] {
  const q = searchFold(query.trim());
  if (q.length < 2) return [];
  const hits: string[] = [];
  for (const it of items) {
    if (it.kind === "message" && !it.message.retracted_at) {
      if (searchFold(`${it.message.body}\n${fileNames(it.message.id).join("\n")}`).includes(q)) hits.push(it.key);
    } else if (it.kind === "note" && searchFold(it.note.body).includes(q)) hits.push(it.key);
  }
  return hits;
}
/** Text in Teile mit und ohne Treffer zerlegen (für <mark>); ohne gültige Suche ein Teil. */
export function highlightParts(text: string, query: string): { text: string; hit: boolean }[] {
  const q = searchFold(query.trim());
  if (q.length < 2 || !text) return [{ text, hit: false }];
  // Vergleich auf der gefalteten Fassung; Zeichen für Zeichen zurück auf den Originaltext abbilden
  const map: number[] = [];
  let folded = "";
  for (let i = 0; i < text.length; i++) {
    const f = searchFold(text[i]!);
    for (let k = 0; k < f.length; k++) map.push(i);
    folded += f;
  }
  const parts: { text: string; hit: boolean }[] = [];
  let pos = 0;
  let from = 0;
  for (;;) {
    const at = folded.indexOf(q, from);
    if (at < 0) break;
    const start = map[at]!;
    const end = (map[at + q.length - 1] ?? text.length - 1) + 1;
    if (start > pos) parts.push({ text: text.slice(pos, start), hit: false });
    parts.push({ text: text.slice(start, end), hit: true });
    pos = end;
    from = at + q.length;
  }
  if (pos < text.length) parts.push({ text: text.slice(pos), hit: false });
  return parts.length ? parts : [{ text, hit: false }];
}

// ── Antworten mit Zitat (K-525) ─────────────────────────────────────────────

/** Zitat über einer Antwort: Anfang der Originalnachricht in einer Zeile. */
export function quoteText(m: Pick<MessageRow, "body" | "retracted_at"> | null | undefined, lang: Lang = currentLang(), max = 120): string {
  if (!m) return lang === "en" ? "Message no longer available" : "Nachricht nicht mehr verfügbar";
  if (m.retracted_at) return lang === "en" ? "Message withdrawn" : "Nachricht zurückgezogen";
  const clean = m.body.replace(/\s+/g, " ").trim() || (lang === "en" ? "Link shared" : "Verknüpfung geteilt");
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()} …` : clean;
}

// ── @-Erwähnung in internen Notizen (K-525) ─────────────────────────────────

export interface Mentionable {
  user_id: string;
  display_name: string;
}
/** Angefangene Erwähnung direkt vor dem Cursor („… @Ki|“): Position des @ und bisher getippter Name; sonst null. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const m = /(?:^|[\s(])@([\p{L}\p{N}._-]*(?: [\p{L}\p{N}._-]*)?)$/u.exec(before);
  if (!m) return null;
  return { start: caret - m[1]!.length - 1, query: m[1]! };
}
/** Vorschläge zur angefangenen Erwähnung: Namen, die mit dem Getippten beginnen (Vor- oder Nachname), höchstens sechs. */
export function mentionSuggestions(people: readonly Mentionable[], query: string, me: string | null): Mentionable[] {
  const q = searchFold(query.trim());
  return people
    .filter((p) => p.user_id !== me)
    .filter((p) => !q || searchFold(p.display_name).split(" ").some((w, i, all) => all.slice(i).join(" ").startsWith(q)))
    .slice(0, 6);
}
/** Gewählte Erwähnung einsetzen: „@Ki“ wird „@Kim Feld “; liefert den neuen Text und die Cursorposition. */
export function insertMention(text: string, start: number, caret: number, name: string): { text: string; caret: number } {
  const insert = `@${name} `;
  return { text: text.slice(0, start) + insert + text.slice(caret), caret: start + insert.length };
}
/** Wer in einer Notiz erwähnt ist: „@Name“ mit vollem Anzeigenamen (längste Namen zuerst, Groß-/Kleinschreibung egal). */
export function parseMentions(text: string, people: readonly Mentionable[]): string[] {
  const folded = searchFold(text);
  const ids: string[] = [];
  for (const p of [...people].sort((a, b) => b.display_name.length - a.display_name.length)) {
    const name = searchFold(p.display_name.trim());
    if (!name) continue;
    let from = 0;
    for (;;) {
      const at = folded.indexOf(`@${name}`, from);
      if (at < 0) break;
      const after = folded[at + name.length + 1];
      if (!after || !/[\p{L}\p{N}]/u.test(after)) {
        if (!ids.includes(p.user_id)) ids.push(p.user_id);
        break;
      }
      from = at + 1;
    }
  }
  return ids;
}
/** Notiztext mit hervorgehobenen Erwähnungen (bekannte Namen) für die Anzeige. */
export function mentionParts(text: string, names: readonly string[]): { text: string; mention: boolean }[] {
  const sorted = [...names].filter((n) => n.trim()).sort((a, b) => b.length - a.length);
  const parts: { text: string; mention: boolean }[] = [];
  let pos = 0;
  let i = 0;
  while (i < text.length) {
    if (text[i] === "@") {
      const rest = text.slice(i + 1).toLowerCase();
      const hit = sorted.find((n) => rest.startsWith(n.toLowerCase()) && !/[\p{L}\p{N}]/u.test(rest[n.length] ?? ""));
      if (hit) {
        if (i > pos) parts.push({ text: text.slice(pos, i), mention: false });
        parts.push({ text: text.slice(i, i + 1 + hit.length), mention: true });
        i += 1 + hit.length;
        pos = i;
        continue;
      }
    }
    i++;
  }
  if (pos < text.length) parts.push({ text: text.slice(pos), mention: false });
  return parts;
}

// ── Hinweis bei neuer Nachricht (K-524) ─────────────────────────────────────

export interface AlertState {
  key: string;
  messages: number;
  latest: string | null;
}
/**
 * Neue Nachricht seit dem letzten Stand? Erster Stand und Wechsel der Agentur melden nichts; gemeldet wird nur, wenn mehr
 * Ungelesenes da ist und die neueste Nachricht eine andere ist.
 */
export function isNewMessage(before: AlertState | null, now: AlertState): boolean {
  if (!before || before.key !== now.key) return false;
  return now.messages > before.messages && !!now.latest && now.latest !== before.latest;
}
/** Anfang des Texts für den Hinweis, eine Zeile. */
export function alertExcerpt(body: string | null | undefined, max = 90): string {
  const clean = (body ?? "").replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()} …` : clean;
}

// ── Bilder vor dem Hochladen verkleinern (K-529 d) ──────────────────────────

/** Längste Kante nach dem Verkleinern */
export const BILD_MAX_KANTE = 2000;
/** Ab dieser Größe wird ein Bild immer neu gespeichert, auch wenn es klein genug ist */
export const BILD_SCHWELLE_BYTES = 1_500_000;
export interface BildPlan {
  width: number;
  height: number;
  type: "image/jpeg" | "image/webp" | "image/png";
  quality: number;
}
/**
 * Soll ein Bild vor dem Hochladen verkleinert werden, und wie? JPEG bleibt JPEG, WebP bleibt WebP, PNG wird WebP (behält
 * Transparenz). HEIC kann der Browser meist nicht lesen: bleibt, wie es ist. null = unverändert hochladen.
 */
export function bildPlan(file: { type: string; size: number }, width: number, height: number, webpOk = true): BildPlan | null {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || width <= 0 || height <= 0) return null;
  const longest = Math.max(width, height);
  if (longest <= BILD_MAX_KANTE && file.size <= BILD_SCHWELLE_BYTES) return null;
  const f = Math.min(1, BILD_MAX_KANTE / longest);
  const type = file.type === "image/jpeg" ? "image/jpeg" : webpOk ? "image/webp" : file.type === "image/png" ? "image/png" : "image/jpeg";
  return { width: Math.max(1, Math.round(width * f)), height: Math.max(1, Math.round(height * f)), type, quality: 0.85 };
}
/** Dateiname zur neuen Art („IMG_1.PNG“ → „IMG_1.webp“) */
export function bildName(name: string, type: BildPlan["type"]): string {
  const ext = type === "image/jpeg" ? "jpg" : type === "image/webp" ? "webp" : "png";
  const base = name.replace(/\.[A-Za-z0-9]{1,5}$/, "");
  return `${base || "bild"}.${ext}`;
}

/**
 * Zustellung eigener Nachrichten (Chat-Blase: Absender, Nachricht, Zeit, Zustellung). Hat der Creator kein Konto im Portal,
 * sieht er die Nachricht nicht; sie erreicht ihn nur über die Sammelmail (Befund 10): „Nur per Mail“ bzw. „Nicht erreichbar“.
 */
export function deliveryLabel(m: Pick<MessageRow, "read_at">, opts: { lang?: Lang; reach?: "portal" | "mail" | "none" } = {}): string {
  const en = opts.lang === "en";
  if (m.read_at) return en ? "Read" : "Gelesen";
  if (opts.reach === "mail") return en ? "By email only" : "Nur per Mail";
  if (opts.reach === "none") return en ? "Not reachable" : "Nicht erreichbar (kein Zugang, keine Adresse)";
  return en ? "Delivered" : "Zugestellt";
}

export interface ReactionChip {
  emoji: string;
  count: number;
  mine: boolean;
}
/** Reaktionen einer Nachricht gebündelt in fester Reihenfolge; `mine` = von der angemeldeten Person. */
export function reactionChips(reactions: readonly ReactionRow[], messageId: string, me: string | null): ReactionChip[] {
  const own = reactions.filter((r) => r.message_id === messageId);
  return EMOJIS.map((emoji) => {
    const list = own.filter((r) => r.emoji === emoji);
    return { emoji, count: list.length, mine: !!me && list.some((r) => r.user_id === me) };
  }).filter((c) => c.count > 0);
}

// ── Dateien (Anhänge der Art 'chat', Bucket documents, 0013) ─────────────────

/** K-525: Word, Excel und PowerPoint (nur im Chat; 0393). Sie erscheinen als Download, nicht als Vorschau. */
export const OFFICE_TYPES = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
} as const;
export const FILE_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", OFFICE_TYPES.docx, OFFICE_TYPES.xlsx, OFFICE_TYPES.pptx] as const;
/** Für die Dateiauswahl: Arten und Endungen (manche Systeme kennen die Office-Arten nicht) */
export const FILE_ACCEPT = [...FILE_TYPES, ".heic", ".docx", ".xlsx", ".pptx"].join(",");
export const MAX_FILE_BYTES = 15_728_640;
export const MAX_FILES = 10;
export const fileHint = (lang: Lang = currentLang()) => (lang === "en"
  ? "PDF, JPG, PNG, WebP, HEIC, Word, Excel or PowerPoint up to 15 MB"
  : "PDF, JPG, PNG, WebP, HEIC, Word, Excel oder PowerPoint bis 15 MB");

/** Dateiart aus dem Browser oder aus der Endung (manche Browser melden HEIC ohne Art, Office-Dateien als ZIP oder leer). */
export function mimeOf(file: { name: string; type: string }): string | null {
  if ((FILE_TYPES as readonly string[]).includes(file.type)) return file.type;
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  const byExt: Record<string, string> = {
    pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", heic: "image/heic", heif: "image/heic",
    docx: OFFICE_TYPES.docx, xlsx: OFFICE_TYPES.xlsx, pptx: OFFICE_TYPES.pptx,
  };
  return byExt[ext] ?? null;
}
/**
 * Im Browser anzeigbar (PDF und Bilder öffnen im neuen Tab); alles andere wird heruntergeladen. Sicherheitsprüfung (Inhaber
 * 02.11.2026): feste Liste statt „image/*“, damit etwa image/svg+xml nie als Seite der App geöffnet wird (lib/sichere-datei.ts).
 */
export const isPreviewable = (mime: string) => oeffenbar(mime);
/** Kurzbezeichnung der Dateiart in der Datei-Karte */
export function fileKindLabel(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime === OFFICE_TYPES.docx) return "Word";
  if (mime === OFFICE_TYPES.xlsx) return "Excel";
  if (mime === OFFICE_TYPES.pptx) return "PowerPoint";
  return mime.replace("image/", "").toUpperCase();
}
export function checkFile(file: { name: string; size: number; type: string }): string | null {
  // Praxistest 13.10.2026: Videos gehören in die Content-Ablage (bis 2 GB); der Chat sagte bisher nur „Format geht nicht“
  if (/^video\//i.test(file.type) || /\.(mp4|m4v|mov|webm|avi|mkv)$/i.test(file.name)) {
    return t(`„${file.name}“ ist ein Video. Videos lädst du an der Kooperation hoch (je nach Tarif bis 500 MB oder 2 GB): als Creator unter „Deine Deals“ mit „Video oder Datei hochladen“, als Agentur unter „Content“.`,
      `“${file.name}” is a video. Upload videos at the collaboration (up to 500 MB or 2 GB depending on the plan): as a creator under “Your deals” with “Upload video or file”, as an agency under “Content”.`);
  }
  if (!mimeOf(file)) return t(`„${file.name}“: Dieses Format geht nicht. Erlaubt sind ${fileHint("de")}.`, `“${file.name}”: this format is not supported. Allowed are ${fileHint("en")}.`);
  if (file.size <= 0) return t(`„${file.name}“ ist leer.`, `“${file.name}” is empty.`);
  if (file.size > MAX_FILE_BYTES) return t(`„${file.name}“ ist größer als 15 MB.`, `“${file.name}” is larger than 15 MB.`);
  return null;
}
/** Anzeigename ohne Pfad- und Steuerzeichen (höchstens 200 Zeichen wie attachments.file_name). */
export function displayFileName(name: string): string {
  const cleaned = name.normalize("NFC").replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").replace(/\s+/g, " ").trim();
  return (cleaned || "datei").slice(-200);
}
/** Speicherpfad nach der Pfadregel '<agentur>/chat/<nachricht>/<datei>' (0013), ohne Sonderzeichen. */
export function chatStoragePath(orgId: string, messageId: string, name: string, stamp: number, index: number): string {
  const stored = displayFileName(name).replace(/[^\p{L}\p{N}._-]+/gu, "-").replace(/\.{2,}/g, ".").replace(/^[.-]+/, "").slice(-120) || "datei";
  return `${orgId}/chat/${messageId}/${stamp}-${index}-${stored}`;
}
export function fileSizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 1 }).format(bytes / 1024 / 1024)} MB`;
}
/** Text einer Nachricht, die nur Dateien trägt (steht auch in der Gesprächsliste). */
export function fileOnlyBody(names: readonly string[]): string {
  if (names.length === 1) return `📎 ${displayFileName(names[0]!)}`;
  return t(`📎 ${names.length} Dateien`, `📎 ${names.length} files`);
}
/** Gespeicherter Text einer Nachricht: der eingegebene Text oder, ohne Text, der Dateihinweis (Leerzeichen am Rand entfallen). */
export function messageBody(text: string, fileNames: readonly string[]): string {
  const clean = text.trim();
  return clean || (fileNames.length ? fileOnlyBody(fileNames) : "");
}
export const isImage = (mime: string) => mime.startsWith("image/") && mime !== "image/heic";

// ── Verknüpfungen (Rechnung, Vertrag, Kooperation desselben Creators) ───────

export interface DealLite {
  id: string;
  title: string;
  status: string;
  brand_id: string;
  /** Startmonat (unterscheidet gleichnamige Kooperationen in der Auswahl, Befund 17) */
  start_month?: string | null;
  visible_to_talent?: boolean;
  archived_at?: string | null;
  created_at: string;
}
export interface InvoiceLite {
  id: string;
  number: string | null;
  status: string;
  kind: string;
  gross_cents: number | null;
  currency: string;
  brand_id: string | null;
  deal_id: string | null;
  created_at: string;
  /** Fälligkeit für die Objekt-Karte im Chat (K-515) */
  effective_due_date?: string | null;
}
export interface ContractLite {
  id: string;
  title: string;
  status: string;
  created_at: string;
  /** Nummer und Marke für den Untertitel der Karte („V-0018 · Glow Cosmetics“) */
  contract_number?: number | null;
  contract_number_label?: string | null;
  brand_id?: string | null;
}
export interface LinkObjects {
  deals: DealLite[];
  invoices: InvoiceLite[];
  contracts: ContractLite[];
  brands: Record<string, string>;
}
export const EMPTY_LINKS: LinkObjects = { deals: [], invoices: [], contracts: [], brands: {} };

export const DEAL_STATUS_LABEL: Record<string, string> = {
  get planning() { return t("In Planung", "In planning"); }, get contract() { return t("Vertrag zur Unterschrift", "Contract awaiting signature"); }, get concept() { return t("Konzept ausstehend", "Concept pending"); }, get production() { return t("In Produktion", "In production"); },
  get content_approval() { return t("Freigabe Content", "Content approval"); }, get published() { return t("Veröffentlicht", "Published"); }, reporting: "Reporting", get invoicing() { return t("Rechnung ausstehend", "Invoice pending"); },
  get done() { return t("Abgeschlossen", "Completed"); }, get blocked() { return t("Blockiert", "Blocked"); }, get cancelled() { return t("Storniert", "Cancelled"); },
};
export const INVOICE_STATUS_LABEL: Record<string, string> = {
  get draft() { return t("Entwurf", "Draft"); }, get submitted() { return t("In Prüfung", "In review"); }, get revision_requested() { return t("Korrektur nötig", "Correction needed"); }, get rejected() { return t("Abgelehnt", "Declined"); }, get approved() { return t("Freigegeben", "Approved"); },
  get sent() { return t("Offen", "Open"); }, get paid() { return t("Bezahlt", "Paid"); }, get cancelled() { return t("Storniert", "Cancelled"); }, get withdrawn() { return t("Zurückgezogen", "Withdrawn"); }, get discarded() { return t("Verworfen", "Discarded"); },
};
export const CONTRACT_STATUS_LABEL: Record<string, string> = {
  get draft() { return t("Entwurf", "Draft"); }, get sent() { return t("Zur Unterschrift", "Awaiting signature"); }, get partially_signed() { return t("Teilweise signiert", "Partially signed"); }, get signed() { return t("Signiert", "Signed"); }, get void() { return t("Aufgehoben", "Rescinded"); },
};
export const LINK_KIND_LABEL: Record<LinkKind, string> = { get deal() { return t("Kooperation", "Deal"); }, get invoice() { return t("Rechnung", "Invoice"); }, get contract() { return t("Vertrag", "Contract"); } };
// Englisch für das Creator-Portal (Befund 9)
const DEAL_STATUS_EN: Record<string, string> = {
  planning: "Planning", contract: "Contract to sign", concept: "Concept pending", production: "In production", content_approval: "Content approval",
  published: "Published", reporting: "Reporting", invoicing: "Invoice pending", done: "Completed", blocked: "Blocked", cancelled: "Cancelled",
};
const INVOICE_STATUS_EN: Record<string, string> = {
  draft: "Draft", submitted: "In review", revision_requested: "Correction needed", rejected: "Rejected", approved: "Approved", sent: "Open",
  paid: "Paid", cancelled: "Cancelled", withdrawn: "Withdrawn", discarded: "Discarded",
};
const CONTRACT_STATUS_EN: Record<string, string> = { draft: "Draft", sent: "To sign", partially_signed: "Partially signed", signed: "Signed", void: "Voided" };
const LINK_KIND_EN: Record<LinkKind, string> = { deal: "Deal", invoice: "Invoice", contract: "Contract" };
const dealStatus = (s: string, lang: Lang) => (lang === "en" ? DEAL_STATUS_EN[s] : DEAL_STATUS_LABEL[s]) ?? s;
const invoiceStatus = (s: string, lang: Lang) => (lang === "en" ? INVOICE_STATUS_EN[s] : INVOICE_STATUS_LABEL[s]) ?? s;
const contractStatus = (s: string, lang: Lang) => (lang === "en" ? CONTRACT_STATUS_EN[s] : CONTRACT_STATUS_LABEL[s]) ?? s;

/**
 * Weg in den Chat mit einem Creator (K-516), auf Wunsch mit vorbelegtem Objekt (K-517 „Im Chat teilen“):
 * /agentur/nachrichten/<creator>?objekt=invoice:<id>
 */
export function chatLink(talentId: string, objekt?: { kind: LinkKind; id: string } | null): string {
  const query = objekt ? new URLSearchParams([["objekt", [objekt.kind, objekt.id].join(":")]]).toString() : "";
  return query ? `/agentur/nachrichten/${talentId}?${query}` : `/agentur/nachrichten/${talentId}`;
}

/** Ziel einer Verknüpfung im jeweiligen Portal (Wege aus den routes.tsx der Gruppen). */
export function linkTarget(kind: LinkKind, id: string, portal: Portal): string {
  if (portal === "agentur") {
    return kind === "deal" ? `/agentur/kooperationen/${id}` : kind === "invoice" ? `/agentur/rechnungen/${id}` : `/agentur/vertraege/${id}`;
  }
  return kind === "deal" ? `/creator/deals/${id}` : kind === "invoice" ? `/creator/rechnungen?rechnung=${id}` : `/creator/vertraege/${id}`;
}

export interface LinkCard {
  title: string;
  sub: string;
  to: string | null;
  /** K-515: Art (Zeichen der Karte) und aktueller Status als Pille */
  kind?: LinkKind;
  pill?: Pill | null;
}

const PILL_SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
const statusPill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: PILL_SYMBOL[tone] });
/** Gutschrift der Agentur an den Creator (Selbstfakturierung) heißt im Chat Gutschrift, nicht Rechnung */
const isCreditNote = (i: InvoiceLite) => i.kind === "agency_to_talent";
/** Überfällig: offen und Fälligkeit vor heute */
const overdue = (i: InvoiceLite, today: string) => i.status === "sent" && !!i.effective_due_date && i.effective_due_date.slice(0, 10) < today;
/**
 * K-515: Status je Objekt als farbige Pille, immer aus dem aktuellen Stand (die Karte liest das Objekt bei jedem Öffnen neu).
 */
export function linkStatusPill(kind: LinkKind, status: string, lang: Lang = currentLang(), opts: { overdue?: boolean } = {}): Pill {
  if (kind === "invoice") {
    if (opts.overdue) return statusPill(lang === "en" ? "Overdue" : "Überfällig", "error");
    const tone: Tone = status === "paid" ? "success" : status === "sent" || status === "approved" ? "info" : status === "revision_requested" ? "warning"
      : status === "rejected" ? "error" : "neutral";
    return statusPill(invoiceStatus(status, lang), tone);
  }
  if (kind === "contract") {
    const tone: Tone = status === "signed" ? "success" : status === "sent" || status === "partially_signed" ? "warning" : "neutral";
    return statusPill(contractStatus(status, lang), tone);
  }
  const tone: Tone = status === "done" || status === "published" ? "success" : status === "blocked" ? "error" : status === "cancelled" ? "neutral"
    : status === "contract" || status === "content_approval" || status === "invoicing" ? "warning" : "info";
  return statusPill(dealStatus(status, lang), tone);
}
/**
 * Objekt-Karte in der Blase (08_komponenten „Objekt-Karte“): Titel, Status, Deep-Link. Fehlt das Objekt (kein Zugriff,
 * gelöscht, für den Creator nicht sichtbar), zeigt die Karte keinen Inhalt und keinen Link.
 */
export function linkCard(kind: LinkKind, id: string, objs: LinkObjects, portal: Portal, talentName: string, lang: Lang = currentLang()): LinkCard {
  const en = lang === "en";
  const na = en ? "Not available" : "Nicht verfügbar";
  const kindLabel = (k: LinkKind) => (en ? LINK_KIND_EN[k] : LINK_KIND_LABEL[k]);
  if (kind === "deal") {
    const d = objs.deals.find((x) => x.id === id);
    if (!d) return { title: kindLabel("deal"), sub: na, to: null };
    const brand = objs.brands[d.brand_id] ?? (en ? "Brand" : "Marke");
    return { title: `${brand} × ${talentName}`, sub: d.title, to: linkTarget("deal", id, portal), kind: "deal", pill: linkStatusPill("deal", d.status, lang) };
  }
  if (kind === "invoice") {
    const i = objs.invoices.find((x) => x.id === id);
    if (!i) return { title: kindLabel("invoice"), sub: na, to: null };
    const brand = i.brand_id ? objs.brands[i.brand_id] : i.deal_id ? objs.brands[objs.deals.find((d) => d.id === i.deal_id)?.brand_id ?? ""] : undefined;
    const due = i.effective_due_date && i.status === "sent" ? (en ? `due ${dateOf(i.effective_due_date, "en")}` : `fällig ${date(i.effective_due_date)}`) : null;
    const sub = [brand, i.gross_cents !== null ? money(i.gross_cents, i.currency) : null, due].filter(Boolean).join(" · ");
    const art = isCreditNote(i) ? (en ? "Credit note" : "Gutschrift") : kindLabel("invoice");
    const today = new Date().toISOString().slice(0, 10);
    return { title: `${art} ${i.number ?? (en ? "without number" : "ohne Nummer")}`, sub, to: linkTarget("invoice", id, portal), kind: "invoice",
      pill: linkStatusPill("invoice", i.status, lang, { overdue: overdue(i, today) }) };
  }
  const c = objs.contracts.find((x) => x.id === id);
  if (!c) return { title: kindLabel("contract"), sub: na, to: null };
  const nummer = c.contract_number_label || (c.contract_number != null ? `V-${String(c.contract_number).padStart(4, "0")}` : null);
  const sub = [nummer, c.brand_id ? objs.brands[c.brand_id] : null].filter(Boolean).join(" · ") || kindLabel("contract");
  return { title: c.title, sub, to: linkTarget("contract", id, portal), kind: "contract", pill: linkStatusPill("contract", c.status, lang) };
}

export interface LinkChoice { value: string; kind: LinkKind; title: string; sub: string; pill: Pill; search: string }
export interface LinkTab { kind: LinkKind; label: string; items: LinkChoice[]; blocked: { title: string; reason: string }[] }
/**
 * K-514: Auswahl „Anhängen“ mit Reitern Kooperation · Rechnung · Vertrag. Wählbar ist nur, was beide Seiten öffnen können;
 * alles andere steht mit Grund darunter, statt still zu fehlen (der Creator sieht keine Rechnungen an Marken, keine
 * Vertragsentwürfe und keine für ihn ausgeblendeten Kooperationen).
 */
export function linkTabs(objs: LinkObjects, portal: Portal, talentName: string, lang: Lang = currentLang()): LinkTab[] {
  const en = lang === "en";
  const brandOf = (id: string | null | undefined) => (id ? objs.brands[id] : undefined);
  const invoiceBrand = (i: InvoiceLite) => brandOf(i.brand_id ?? objs.deals.find((d) => d.id === i.deal_id)?.brand_id);
  const choice = (kind: LinkKind, id: string): LinkChoice => {
    const c = linkCard(kind, id, objs, portal, talentName, lang);
    return { value: `${kind}:${id}`, kind, title: c.title, sub: c.sub, pill: c.pill!, search: `${c.title} ${c.sub} ${c.pill?.label ?? ""}`.toLowerCase() };
  };
  const agentur = portal === "agentur";
  const dealOk = (d: DealLite) => (d.visible_to_talent ?? true) && !d.archived_at;
  const invoiceOk = (i: InvoiceLite) => i.kind !== "agency_to_brand" && i.status !== "discarded";
  const dealTitle = (d: DealLite) => [brandOf(d.brand_id), d.title].filter(Boolean).join(" · ");
  const invoiceTitle = (i: InvoiceLite) => [`${isCreditNote(i) ? (en ? "Credit note" : "Gutschrift") : en ? "Invoice" : "Rechnung"} ${i.number ?? (en ? "without number" : "ohne Nummer")}`, invoiceBrand(i)].filter(Boolean).join(" · ");
  return [
    {
      kind: "deal", label: en ? "Deals" : "Kooperationen",
      items: objs.deals.filter(dealOk).map((d) => choice("deal", d.id)),
      blocked: agentur ? objs.deals.filter((d) => !dealOk(d)).map((d) => ({ title: dealTitle(d),
        reason: d.archived_at ? (en ? "Archived" : "Archiviert") : en ? "Hidden from the creator" : "Für den Creator ausgeblendet" })) : [],
    },
    {
      kind: "invoice", label: en ? "Invoices & credit notes" : "Rechnungen & Gutschriften",
      items: objs.invoices.filter(invoiceOk).map((i) => choice("invoice", i.id)),
      // Verworfene Entwürfe sind nur Rauschen und fehlen ganz; Rechnungen an Marken stehen mit Grund da
      blocked: agentur ? objs.invoices.filter((i) => i.kind === "agency_to_brand" && i.status !== "discarded").map((i) => ({ title: invoiceTitle(i),
        reason: en ? "Invoice to the brand: the creator does not see it" : "Rechnung an die Marke: sieht der Creator nicht" })) : [],
    },
    {
      kind: "contract", label: en ? "Contracts" : "Verträge",
      items: objs.contracts.filter((c) => c.status !== "draft").map((c) => choice("contract", c.id)),
      blocked: agentur ? objs.contracts.filter((c) => c.status === "draft").map((c) => ({ title: c.title,
        reason: en ? "Draft: the creator sees it only after sending" : "Entwurf: sieht der Creator erst nach dem Versand" })) : [],
    },
  ];
}

export interface LinkOptionGroup {
  label: string;
  options: { value: string; label: string }[];
}
/**
 * Auswahl „Verknüpfen“: nur Objekte, die beide Seiten öffnen können. Das Team verweist deshalb nicht auf
 * Vertragsentwürfe, Rechnungen an Marken, ausgeblendete oder archivierte Kooperationen (der Creator sähe sie nicht).
 */
export function linkOptions(objs: LinkObjects, lang: Lang = currentLang()): LinkOptionGroup[] {
  const en = lang === "en";
  const deals = objs.deals.filter((d) => (d.visible_to_talent ?? true) && !d.archived_at);
  const invoices = objs.invoices.filter((i) => i.kind !== "agency_to_brand" && i.status !== "discarded");
  const contracts = objs.contracts.filter((c) => c.status !== "draft");
  const brand = (id: string | null | undefined) => (id ? objs.brands[id] : undefined);
  const invoiceBrand = (i: InvoiceLite) => brand(i.brand_id ?? objs.deals.find((d) => d.id === i.deal_id)?.brand_id);
  // Gleichnamige Kooperationen (gleiche Marke, gleicher Titel) unterscheidet der Startmonat bzw. das Anlagedatum (Befund 17)
  const baseLabel = (d: DealLite) => [brand(d.brand_id), d.title].filter(Boolean).join(" · ");
  const counts = new Map<string, number>();
  for (const d of deals) counts.set(baseLabel(d), (counts.get(baseLabel(d)) ?? 0) + 1);
  const when = (d: DealLite) => (d.start_month
    ? (en ? `from ${monthOf(d.start_month.slice(0, 7), "en")}` : `ab ${monthLabel(d.start_month.slice(0, 7))}`)
    : d.created_at ? (en ? `created ${dateOf(d.created_at, "en")}` : `angelegt ${date(d.created_at)}`) : null);
  const dealLabel = (d: DealLite) => [baseLabel(d), (counts.get(baseLabel(d)) ?? 0) > 1 ? when(d) : null, dealStatus(d.status, lang)].filter(Boolean).join(" · ");
  const groups: LinkOptionGroup[] = [
    { label: en ? "Deals" : "Kooperationen", options: deals.map((d) => ({ value: `deal:${d.id}`, label: dealLabel(d) })) },
    { label: en ? "Invoices" : "Rechnungen", options: invoices.map((i) => ({ value: `invoice:${i.id}`, label: [`${en ? "Invoice" : "Rechnung"} ${i.number ?? (en ? "without number" : "ohne Nummer")}`, invoiceBrand(i), invoiceStatus(i.status, lang)].filter(Boolean).join(" · ") })) },
    { label: en ? "Contracts" : "Verträge", options: contracts.map((c) => ({ value: `contract:${c.id}`, label: `${c.title} · ${contractStatus(c.status, lang)}` })) },
  ];
  return groups.filter((g) => g.options.length > 0);
}
export function parseLinkValue(value: string): { kind: LinkKind; id: string } | null {
  const m = /^(deal|invoice|contract):([0-9a-f-]{36})$/.exec(value);
  return m ? { kind: m[1] as LinkKind, id: m[2]! } : null;
}
/** Kontext-Pille im Kopf des Verlaufs: das zuletzt verknüpfte Objekt (Paket: „○ Lunava · September“). */
export function threadContext(messages: readonly MessageRow[], objs: LinkObjects): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (!m.link_kind || !m.link_id) continue;
    if (m.link_kind === "deal") {
      const d = objs.deals.find((x) => x.id === m.link_id);
      if (d) return [objs.brands[d.brand_id], d.title].filter(Boolean).join(" · ");
    } else if (m.link_kind === "invoice") {
      const inv = objs.invoices.find((x) => x.id === m.link_id);
      if (inv) return t(`Rechnung ${inv.number ?? t("ohne Nummer", "without number")}`, `Invoice ${inv.number ?? t("ohne Nummer", "without number")}`);
    } else {
      const c = objs.contracts.find((x) => x.id === m.link_id);
      if (c) return t(`Vertrag · ${c.title}`, `Contract · ${c.title}`);
    }
  }
  return null;
}

// ── Benachrichtigungen ──────────────────────────────────────────────────────

/** Sicht my_notifications (offene Aufgaben und nicht ausgeblendete Hinweise der angemeldeten Person). */
export interface NotificationRow {
  id: string;
  org_id: string;
  type: string;
  is_task: boolean;
  color: string;
  audience: "staff" | "talent";
  talent_id: string | null;
  params: Record<string, unknown> | null;
  link_kind: string | null;
  link_id: string | null;
  created_at: string;
  read_at: string | null;
  unread: boolean;
  sort_order: number;
  /** Aufgabe übernommen von (0056, Befund 16) */
  assigned_to?: string | null;
  assigned_name?: string | null;
}
export type NotificationState = "open" | "resolved" | "dismissed";
export interface NotificationItem extends NotificationRow {
  state: NotificationState;
  /** Erledigt (Aufgabe) bzw. ausgeblendet (Hinweis); null bei offenen */
  closed_at: string | null;
  /** Grund, wenn das Team die Aufgabe von Hand geschlossen hat oder ihr Anlass weggefallen ist (0041) */
  resolve_reason?: string | null;
}

export type Category = "rechnungen" | "zahlungen" | "vertraege" | "termine" | "creator" | "mails" | "team";
export const CATEGORY_LABEL: Record<Category, string> = {
  get rechnungen() { return t("Rechnungen", "Invoices"); }, get zahlungen() { return t("Zahlungen", "Payments"); }, get vertraege() { return t("Verträge", "Contracts"); }, get termine() { return t("Fristen und Termine", "Deadlines and appointments"); }, creator: "Creator", get mails() { return t("Mails", "Emails"); }, get team() { return ta("Team und Sicherheit", "Team and security", "Konto und Sicherheit", "Account and security"); },
};
export const CATEGORY_LABEL_EN: Record<Category, string> = {
  rechnungen: "Invoices", zahlungen: "Payments", vertraege: "Contracts", termine: "Deadlines and dates", creator: "Creators", mails: "Emails", team: "Team and security",
};
export function categoryLabel(c: Category, lang: Lang = currentLang()): string {
  return lang === "en" ? CATEGORY_LABEL_EN[c] : CATEGORY_LABEL[c];
}
/** Art je Meldungstyp (Typ-Katalog notification_types). Test: jeder Typ der Datenbank steht hier (Befund 5). */
export const TYPE_CATEGORY: Record<string, Category> = {
  invoice_request: "rechnungen", invoice_revision: "rechnungen", invoice_rejected: "rechnungen", invoice_submitted: "rechnungen", invoice_rejection_reverted: "rechnungen",
  invoice_send_overdue: "rechnungen", invoice_overdue: "rechnungen", dunning_deadline_expired: "rechnungen",
  // 0570 Mahn-Autopilot
  dunning_auto_sent: "rechnungen",
  payment_check: "zahlungen", payment_confirmed: "zahlungen", payment_reset: "zahlungen", payout_revoked: "zahlungen",
  payment_reported: "zahlungen", payment_missing: "zahlungen",
  // 0076: überfällige Auszahlung an den Creator (Recht „Auszahlungen“)
  payout_overdue: "zahlungen",
  contract_to_sign: "vertraege", contract_completed: "vertraege", contract_awaiting_countersign: "vertraege",
  contract_signed_by_party: "vertraege", contract_declined: "vertraege",
  calendar_event: "termine", talent_joined: "creator",
  // 0086 Creator hat sein Konto löschen lassen (CREATOR-R2-3)
  talent_left_portal: "creator",
  // 0041
  invoice_mail_failed: "mails", mail_failed: "mails", invoice_missing: "rechnungen", usage_rights_expiring: "termine",
  collection_handover: "zahlungen", invoice_in_collection: "zahlungen", content_deadline_soon: "termine", calendar_event_cancelled: "termine",
  // 0040 Verträge, 0043 Rechnungsablauf
  contract_stopped: "vertraege", contract_voided: "vertraege", contract_terminated: "vertraege", contract_deal_cancelled: "vertraege",
  contract_link_expired: "vertraege", contract_deadline: "vertraege", invoice_cancelled: "rechnungen",
  // 0093 (CREATOR-R3-2): minderjährig, gesetzliche Vertretung fehlt
  contract_minor_guardian: "vertraege",
  // 0039 Rechnung der Agentur, 0045 Bankverbindung, 0046 Gutschriftverfahren
  agency_invoice_issued: "rechnungen",
  payout_details_changed: "zahlungen", bank_details_changed: "zahlungen", self_billing_issued: "rechnungen", self_billing_cancelled: "rechnungen", self_billing_objected: "rechnungen",
  // 0056
  creator_mail_failed: "mails", talent_no_email: "mails", mail_complaint: "mails", calendar_response: "termine", content_deadline_changed: "termine",
  // 0062 Steuerstatus mit Gültig-ab
  tax_status_check: "rechnungen", org_tax_status_check: "rechnungen",
  // 0079 Steuerstatus rückwirkend geändert (CREATOR-5)
  tax_status_retro: "rechnungen",
  // 0079 Nachfrage eines archivierten Creators zur Auszahlung (CREATOR-6)
  payout_question_archived: "zahlungen",
  // 0057 Support-Zugang und Chef-Rolle
  support_access_opened: "team", support_session_started: "team", chef_role_changed: "team",
  // 0068 Beiträge
  deliverable_submitted: "termine", deliverable_live_reported: "termine", deliverables_overdue: "termine", deliverable_draft_due: "termine", deliverable_publish_due: "termine", deliverable_overdue_talent: "termine",
  // 0072 Anfragen und Angebote
  offer_follow_up: "termine",
  // 0073 Freigabe durch die Marke, Kennzahlen
  brand_review_approved: "termine", brand_review_changes: "termine", brand_review_forward: "termine", brand_review_expired: "termine",
  deliverable_metrics_submitted: "termine", brand_approved: "termine", brand_changes: "termine", content_changes: "termine", deliverable_metrics_due: "termine",
  // 0074 Media-Kit
  kit_proposal: "creator",
  // 0077 Korrekturrunde 1
  org_readonly_notice: "team", member_mfa_reset: "team", contract_resumed: "vertraege", tax_status_check_team: "rechnungen",
  deliverable_deadline_changed: "termine",
  // 0085 neuer bzw. gestrichener Beitrag an den Creator
  deliverable_added: "termine", deliverable_cancelled: "termine",
  // 0458 Prüf-Chat: neue Kooperation (CP-014), Rechnung freigegeben (CP-022)
  deal_new: "termine", invoice_approved: "rechnungen",
  // 0080 Belege nachreichen
  invoice_receipt_added: "rechnungen",
  // 0092 Laufzeitende eines von Hand freigeschalteten Abos
  subscription_expiry: "team",
  // 0132 Lastschrift für das Abo gescheitert (verzögerte Zahlung)
  subscription_payment_failed: "team",
  // 0134 Speicherkontingent ab 80 %
  storage_quota: "team",
  // 0094 Übergabe an das Inkasso zurückgenommen (AUTOMATIK-R3-2)
  invoice_collection_withdrawn: "zahlungen",
  // 0102 eigene Absenderdomain (nur Admins)
  sender_domain_verified: "mails", sender_domain_failed: "mails", sender_domain_removed: "mails",
  // 0104 Rückläuferquote der letzten 7 Tage (nur Admins)
  mail_bounce_rate: "mails",
  // 0166 WhatsApp: Creator hat per STOP abbestellt, Verbindung der Nummer gestört
  whatsapp_stop: "mails", whatsapp_account_error: "mails",
  // 0393 Erwähnung in einer internen Notiz im Chat (nur für die erwähnte Person im Team)
  chat_mention: "team",
};

/**
 * Creator-Meldungen, deren Mail immer sofort kommt, auch beim Bündeln und mit abbestellten Hinweisen
 * (notification_types.mail_policy = 'always'). Ein Test gleicht die Liste mit der Datenbank ab (AUTOMATIK-R3-10); wer sie
 * ändert, passt auch den Satz in creatorMailHints an.
 */
export const CREATOR_MAIL_IMMEDIATE = [
  "agency_invoice_issued", "self_billing_issued", "self_billing_cancelled", "invoice_cancelled", "contract_terminated", "contract_voided", "contract_stopped",
  "invoice_in_collection", "invoice_collection_withdrawn", "payment_reset", "payout_revoked", "org_readonly_notice", "bank_details_changed",
  // 0155: Korrektur am Entwurf („Korrektur ⇒ immer E-Mail“)
  "content_changes",
] as const;
/** Creator-Meldungen mit Mail, die sich nicht abbestellen lassen, aber gebündelt kommen dürfen (mail_policy = 'task'). */
export const CREATOR_MAIL_TASK = [
  "invoice_request", "invoice_revision", "invoice_rejected", "invoice_rejection_reverted", "payment_check", "tax_status_check", "deliverable_metrics_due",
  "content_deadline_changed", "deliverable_added", "deliverable_cancelled", "deliverable_deadline_changed", "brand_approved", "brand_changes",
  "contract_resumed",
] as const;

/** Sätze unter „E-Mail für Hinweise“ und „Mails bündeln“ im Creator-Portal: was trotzdem bzw. immer sofort kommt. */
export function creatorMailHints(lang: Lang): { info: string; immediate: string } {
  return lang === "en"
    ? {
      info: "Notices are: new collaborations, approved invoices, confirmed payments, fully signed contracts, new and changed dates and cancellations, reminders before deadlines. You can switch them off at any time. "
        + "The following always arrive by email: tasks (invoice requests, corrections, payment checks, tax status, metrics, contracts to sign), changed deadlines and posts, "
        + "feedback from the brand, withdrawn rejections, a contract that continues, and everything that always arrives right away (see “Bundle emails”).",
      immediate: "Always right away: invoices, self-billing invoices and cancellations from your agency, termination, voiding or suspension of a contract, "
        + "handover to debt collection and its withdrawal, reset payments, withdrawn payouts, your agency switching to read-only, the warning when your bank details change, and change requests for your drafts.",
    }
    : {
      info: "Hinweise sind: neue Kooperationen, freigegebene Rechnungen, bestätigte Zahlungen, fertig unterschriebene Verträge, neue und geänderte Termine und Absagen, Erinnerungen vor Abgaben. Abschalten geht jederzeit. "
        + "Immer per E-Mail kommen dagegen Aufgaben (Rechnung stellen oder korrigieren, Zahlungsnachfragen, Steuerstatus, Kennzahlen, Verträge zur Unterschrift), geänderte Deadlines "
        + "und Beiträge, Rückmeldungen der Marke, zurückgenommene Ablehnungen, ein Vertrag, der weiterläuft, und alles, was immer sofort kommt (siehe „Mails bündeln“).",
      immediate: "Immer sofort: Rechnungen, Gutschriften und Stornos deiner Agentur, Kündigung, Aufhebung oder Anhalten eines Vertrags, "
        + "Übergabe an das Inkasso und ihre Rücknahme, zurückgesetzte Zahlungen, zurückgenommene Auszahlungen, der Nur-Lesen-Betrieb deiner Agentur, die Warnung, wenn deine Bankverbindung geändert wurde, und Korrekturwünsche zu deinen Entwürfen.",
    };
}

/** Beiträge aus den Parametern einer Meldung (0068: items mit Format, Plattform, Titel) als kurze Liste. */
function beitragsListe(v: unknown, lang: Lang, max = 3): string | null {
  if (!Array.isArray(v)) return null;
  const plat = (k: string) => (k === "other" ? (lang === "en" ? "Other" : "Weitere") : PLATFORM[k] ?? k);
  const list = v.filter((x): x is Record<string, unknown> => !!x && typeof x === "object")
    .map((x): DeliverableLike => ({ status: "planned", format: String(x.format ?? "other"), format_label: typeof x.format_label === "string" ? x.format_label : null,
      platform: String(x.platform ?? ""), title: typeof x.title === "string" ? x.title : null }));
  if (list.length > max) {
    const shown = list.slice(0, max).map((x) => deliverableLabel(x, plat, lang)).join(", ");
    return lang === "en" ? `${shown} and ${list.length - max} more` : `${shown} und ${list.length - max} weitere`;
  }
  return summarizeDeliverables(list, plat, lang);
}
/** Arten, die es im jeweiligen Portal gibt (Reihenfolge der Filterknöpfe). */
export const CATEGORIES: Record<Portal, Category[]> = {
  agentur: ["rechnungen", "zahlungen", "vertraege", "termine", "creator", "mails", "team"],
  creator: ["rechnungen", "zahlungen", "vertraege", "termine"],
};

/** Was zur Rechnung einer Team-Meldung nachgeladen wird (Nummer, Marke, Betrag, Fälligkeit). */
export interface InvoiceFacts {
  number: string | null;
  brand: string | null;
  gross_cents: number | null;
  currency: string;
  due: string | null;
}
export interface NotificationContext {
  portal: Portal;
  today: string;
  timeZone: string;
  talentName: (id: string | null) => string | null;
  invoice: (id: string | null) => InvoiceFacts | undefined;
  /** Sprache der betrachtenden Person (Creator-Portal: eigene Wahl; Agentur: Deutsch) */
  lang?: Lang;
}
export interface NotificationView {
  item: NotificationItem;
  title: string;
  detail: string;
  to: string | null;
  category: Category | null;
  pills: Pill[];
  /** Hinweise lassen sich ausblenden, Aufgaben verschwinden erst, wenn sie erledigt sind (A-25). */
  dismissible: boolean;
  /** Zahlungsnachfrage: direkt in der Meldung beantworten (TA-166) */
  answerInvoice: string | null;
  search: string;
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
/** Meldung zu einem Beteiligten in der Schweiz oder in Liechtenstein (Parameter vat_country, 0087): MWST-Begriffe */
const vatCh = (p: Record<string, unknown>): boolean => ["CH", "LI"].includes(String(p.vat_country ?? "").toUpperCase());
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);

function amount(p: Record<string, unknown>, inv?: InvoiceFacts): string | null {
  const cents = num(p.amount_cents) ?? inv?.gross_cents ?? null;
  if (cents === null) return null;
  return money(cents, str(p.currency) ?? inv?.currency ?? "EUR");
}

/** Wie viele Tage ist die Rechnung einer Aufgabe überfällig (0 = nicht überfällig oder unbekannt)? */
export function overdueDays(due: string | null | undefined, today: string): number {
  if (!due || !/^\d{4}-\d{2}-\d{2}$/.test(due)) return 0;
  return Math.max(0, daysBetween(due, today));
}

/**
 * Uhrzeit eines Termins in der Zone der betrachtenden Person (Befund 15): „16.10.2026 · 14:00 Uhr“; war der Termin in
 * einer anderen Zone angelegt, steht „deine Zeit“ dabei. Ohne Uhrzeit nur das Datum.
 */
export function terminZeit(p: Record<string, unknown>, viewerZone: string, lang: Lang = currentLang()): string | null {
  const day = str(p.date);
  if (!day) return null;
  const fmt = (d: string) => (lang === "en" ? dateOf(d, "en") : date(d));
  const start = str(p.start_time)?.slice(0, 5) ?? null;
  if (!start || !/^\d{2}:\d{2}$/.test(start)) return fmt(day);
  const zone = str(p.time_zone);
  const conv = zone && zone !== viewerZone && /^\d{4}-\d{2}-\d{2}$/.test(day) ? convertTime(day, start, zone, viewerZone) : null;
  if (conv && (conv.time !== start || conv.day !== day)) {
    return lang === "en" ? `${fmt(conv.day)} · ${conv.time} your time` : `${fmt(conv.day)} · ${conv.time} Uhr deine Zeit`;
  }
  return lang === "en" ? `${fmt(day)} · ${start}` : `${fmt(day)} · ${start} Uhr`;
}

/** Titel, Zeile, Ziel, Art und Pillen einer Meldung. Texte aus Typ und Parametern (die Datenbank speichert keinen fertigen Text). */
export function viewNotification(item: NotificationItem, ctx: NotificationContext): NotificationView {
  const lang: Lang = ctx.lang ?? currentLang();
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const fmtDate = (d: string) => (lang === "en" ? dateOf(d, "en") : date(d));
  const month = (key: string | null) => (key && /^\d{4}-\d{2}/.test(key) ? (lang === "en" ? monthOf(key.slice(0, 7), "en") : monthLabel(key.slice(0, 7))) : null);
  const p = item.params ?? {};
  const id = item.link_id;
  const talent = ctx.talentName(item.talent_id);
  const inv = item.link_kind === "invoice" ? ctx.invoice(id) : undefined;
  const brand = str(p.brand) ?? inv?.brand ?? null;
  const A = ctx.portal === "agentur";
  const invoiceNo = () => {
    const n = str(p.number) ?? inv?.number ?? null;
    return n ? `${t("Rechnung", "Invoice")} ${n}` : t("Rechnung", "Invoice");
  };
  let title = t("Benachrichtigung", "Notification");
  let parts: (string | null)[] = [];
  let to: string | null = null;
  let due: string | null = null;
  const toInvoice = id ? (A ? `/agentur/rechnungen/${id}` : `/creator/rechnungen?rechnung=${id}`) : null;
  const toContract = id ? (A ? `/agentur/vertraege/${id}` : `/creator/vertraege/${id}`) : null;
  const toTalent = item.talent_id ? `/agentur/creator/${item.talent_id}` : "/agentur/creator";
  const role = str(p.role);

  switch (item.type) {
    // ── an das Team ──
    case "invoice_submitted":
      title = p.reverted === true ? t("Rechnung wieder in Prüfung (Ablehnung zurückgenommen)", "Invoice back in review (rejection withdrawn)")
        : p.again === true ? t("Rechnung erneut eingereicht", "Invoice resubmitted") : t("Rechnung eingereicht", "Invoice submitted");
      parts = [talent, invoiceNo(), brand, amount(p, inv)];
      to = toInvoice;
      break;
    case "invoice_send_overdue":
      title = t("Versand überfällig", "Sending overdue");
      parts = [invoiceNo(), brand, amount(p, inv), t("freigegeben, aber noch nicht an die Marke gesendet", "approved but not yet sent to the brand")];
      to = toInvoice;
      break;
    case "invoice_overdue":
      title = t("Rechnung ist überfällig", "Invoice is overdue");
      parts = [invoiceNo(), brand, amount(p, inv), talent];
      to = id ? `/agentur/rechnungen/${id}/erinnern` : null;
      due = inv?.due ?? str(p.due_date);
      break;
    case "payout_overdue":
      // Schuld der Agentur an den Creator (AUTOMATIK-3): ohne Marke, zur Rechnung (dort „In den Zahlungslauf“ bzw. Auszahlungen)
      title = t("Auszahlung überfällig", "Payout overdue");
      parts = [talent, invoiceNo(), amount(p, inv), t("Auszahlung an den Creator", "Payout to the creator")];
      to = toInvoice;
      due = str(p.due_date) ?? inv?.due ?? null;
      break;
    case "dunning_deadline_expired":
      title = t("Mahnfrist abgelaufen", "Dunning deadline passed");
      parts = [invoiceNo(), brand, amount(p, inv), t("nächste Mahnstufe prüfen", "check next dunning level")];
      to = id ? `/agentur/rechnungen/${id}/erinnern` : null;
      break;
    case "dunning_auto_sent":
      // 0570: Mahn-Autopilot hat die fällige Stufe gesendet
      title = t("Mahnung automatisch gesendet", "Dunning notice sent automatically");
      parts = [invoiceNo(), brand, str(p.recipient), str(p.deadline) ? t(`Frist bis ${date(String(p.deadline))}`, `deadline ${date(String(p.deadline))}`) : null];
      to = toInvoice;
      break;
    case "invoice_receipt_added":
      title = t("Beleg nachgereicht", "Document submitted later");
      parts = [talent, invoiceNo(), str(p.file_name)];
      to = toInvoice;
      break;
    case "payment_reported":
      // CREATOR-R3-6 (0091): bei einer Rechnung der Agentur an den Creator meldet er seine Überweisung
      title = p.kind === "agency_to_talent" ? t("Creator meldet Überweisung", "Creator reports transfer") : t("Creator meldet Zahlungseingang", "Creator reports payment received");
      parts = [talent, invoiceNo(), amount(p, inv), p.kind === "agency_to_talent" ? t("Zahlungseingang erfassen", "Record incoming payment") : t("Zahlung bestätigen", "Confirm payment")];
      to = toInvoice;
      break;
    // 0079: früherer Creator (archiviert, Chat zu) fragt nach einer offenen Auszahlung; Antwort per Mail
    case "payout_question_archived":
      title = t("Früherer Creator fragt nach der Auszahlung", "Former creator asks about the payout");
      parts = [talent, invoiceNo(), amount(p, inv), str(p.message) ? `„${str(p.message)}“` : null, str(p.reply_to) ? t(`Antwort per Mail an ${str(p.reply_to)}`, `Reply by email to ${str(p.reply_to)}`) : t("per Mail antworten", "reply by email")];
      to = toInvoice;
      break;
    case "payment_missing":
      title = t("Creator: Geld nicht angekommen", "Creator: money not received");
      parts = [talent, invoiceNo(), amount(p, inv), t("trotz bestätigter Auszahlung", "despite confirmed payout")];
      to = toInvoice;
      break;
    case "contract_minor_guardian":
      title = t("Minderjährig: gesetzliche Vertretung fehlt", "Minor: legal guardian missing");
      parts = [str(p.title), talent, p.signed === true
        ? t("schon unterschrieben, aber ohne Eltern: Genehmigung der gesetzlichen Vertretung einholen oder neu schließen", "already signed, but without the parents: obtain the legal guardian's approval or conclude it again")
        : t("Unterschreiben gesperrt: neue Fassung mit den Eltern als Unterzeichner senden", "signing locked: send a new version with the parents as signatories")];
      to = toContract;
      break;
    case "contract_awaiting_countersign":
      title = t("Vertrag wartet auf deine Unterschrift", "Contract awaiting your signature");
      parts = [str(p.title), talent];
      to = toContract;
      break;
    case "contract_signed_by_party":
      title = role === "talent" ? t("Vertrag vom Creator unterschrieben", "Contract signed by the creator") : role === "brand" ? t("Vertrag von der Marke unterschrieben", "Contract signed by the brand")
        : role === "agency" ? t("Vertrag von der Agentur unterschrieben", "Contract signed by the agency") : t("Vertrag unterschrieben", "Contract signed");
      parts = [str(p.title), talent];
      to = toContract;
      break;
    case "contract_declined":
      title = role === "brand" ? t("Marke hat den Vertrag abgelehnt", "Brand declined the contract") : role === "talent" ? t("Creator hat den Vertrag abgelehnt", "Creator declined the contract") : t("Vertrag abgelehnt", "Contract declined");
      parts = [str(p.title), talent, str(p.reason) ? t(`Grund: ${str(p.reason)}`, `Reason: ${str(p.reason)}`) : null];
      to = toContract;
      break;
    case "invoice_mail_failed":
      title = p.bounced === true ? t("Mail zur Rechnung zurückgekommen", "Invoice email bounced") : t("Mail zur Rechnung nicht zugestellt", "Invoice email not delivered");
      parts = [invoiceNo(), str(p.recipient) ? t(`an ${str(p.recipient)}`, `to ${str(p.recipient)}`) : null, str(p.error), t("Adresse prüfen und erneut senden", "check the address and send again")];
      to = toInvoice;
      break;
    case "mail_failed":
      title = p.bounced === true ? t("Mail zurückgekommen", "Email bounced") : t("Mail nicht zugestellt", "Email not delivered");
      parts = [talent, str(p.recipient) ? t(`an ${str(p.recipient)}`, `to ${str(p.recipient)}`) : null, str(p.error)];
      to = id ? `/agentur/postausgang?auftrag=${id}` : "/agentur/postausgang";
      break;
    case "creator_mail_failed":
      title = p.bounced === true ? t("Mail an Creator zurückgekommen", "Email to creator bounced") : t("Mail an Creator nicht zugestellt", "Email to creator not delivered");
      parts = [talent, str(p.recipient) ? t(`an ${str(p.recipient)}`, `to ${str(p.recipient)}`) : null, str(p.error), t("Adresse im Profil prüfen", "Check the address in the profile")];
      to = toTalent;
      break;
    case "talent_no_email":
      title = t("Creator ohne E-Mail-Adresse", "Creator without email address");
      parts = [talent, t("Mails an diesen Creator gehen nicht raus; Adresse im Profil eintragen, wartende Mails gehen dann automatisch raus", "Emails to this creator are not sent; enter an address in the profile and waiting emails are sent automatically")];
      to = toTalent;
      break;
    // 0104: Rückläuferquote der letzten 7 Tage über der Schwelle der Plattform
    case "mail_bounce_rate":
      title = t("Viele Mails kommen zurück", "Many emails bounce");
      parts = [p.bounced !== undefined && p.total !== undefined
        ? t(`${String(p.bounced)} von ${String(p.total)} Mails der letzten 7 Tage zurückgekommen (${String(p.percent).replace(".", ",")} %)`,
          `${String(p.bounced)} of ${String(p.total)} emails of the last 7 days bounced (${String(p.percent)} %)`) : null,
        t(`Adressen im Postausgang prüfen und korrigieren; über ${String(p.threshold ?? 5)} % gefährden Rückläufer die Zustellung aller Mails`,
          `check and correct addresses in the outbox; above ${String(p.threshold ?? 5)} % bounces put the delivery of all emails at risk`)];
      to = "/agentur/postausgang";
      break;
    // 0102: eigene Absenderdomain (Stand kommt vom Versanddienst)
    case "sender_domain_verified":
      title = t("Eigene Absenderdomain bestätigt", "Custom sender domain verified");
      parts = [str(p.domain), str(p.address) ? t(`Absender ${str(p.address)}`, `sender ${str(p.address)}`) : null];
      to = "/agentur/einstellungen/vorlagen";
      break;
    case "sender_domain_failed":
      title = t("Absenderdomain: Prüfung gescheitert", "Sender domain: verification failed");
      parts = [str(p.domain), str(p.error), t("DNS-Einträge prüfen, dann „Jetzt prüfen“", "check the DNS records, then “Check now”")];
      to = "/agentur/einstellungen/vorlagen";
      break;
    case "sender_domain_removed":
      // K-385 (0289): Tarif ohne eigene Domain: pausiert, DNS bleibt; mit dem Upgrade sofort wieder aktiv
      if (p.reason === "paused") {
        title = t("Eigene Absenderdomain pausiert", "Custom sender domain paused");
        parts = [str(p.domain), t("Tarif ohne eigene Absenderdomain; Mails gehen bis zum Upgrade über die Absenderadresse der Plattform, die Einrichtung bleibt erhalten", "plan without custom sender domain; until you upgrade, emails go out via the platform's sender address, the setup is kept")];
        to = "/agentur/einstellungen/abo";
        break;
      }
      title = t("Eigene Absenderdomain entfernt", "Custom sender domain removed");
      parts = [str(p.domain), p.reason === "plan" ? t("Tarif ohne eigene Absenderdomain; Mails gehen über die Absenderadresse der Plattform", "plan without custom sender domain; emails go out via the platform's sender address") : null];
      to = "/agentur/einstellungen/vorlagen";
      break;
    // 0166: WhatsApp
    case "whatsapp_stop":
      title = t("WhatsApp abbestellt", "WhatsApp unsubscribed");
      parts = [talent, t("Der Creator hat per STOP abbestellt; WhatsApp-Hinweise an ihn sind aus, Mails und Glocke bleiben", "The creator unsubscribed via STOP; WhatsApp notices to them are off, emails and bell remain")];
      to = toTalent;
      break;
    case "whatsapp_account_error":
      title = t("WhatsApp-Verbindung gestört", "WhatsApp connection problem");
      parts = [str(p.reason), t("Nummer in den Einstellungen neu verbinden; bis dahin gehen keine WhatsApp-Hinweise raus", "reconnect the number in the settings; until then no WhatsApp notices are sent")];
      to = "/agentur/einstellungen/whatsapp";
      break;
    // 0393: Erwähnung in einer internen Notiz (sieht nur das Team)
    case "chat_mention":
      title = t(`${str(p.author) ?? "Team"} hat dich erwähnt`, `${str(p.author) ?? "Team"} mentioned you`);
      parts = [talent ? t(`Interne Notiz zu ${talent}`, `Internal note on ${talent}`) : t("Interne Notiz", "Internal note"), str(p.excerpt) ? `„${str(p.excerpt)}“` : null];
      to = item.talent_id ? `/agentur/nachrichten/${item.talent_id}${str(p.note_id) ? `?notiz=${str(p.note_id)}` : ""}` : "/agentur/nachrichten";
      break;
    case "mail_complaint":
      title = t("Mail als Spam gemeldet", "Email reported as spam");
      parts = [talent, str(p.recipient), t("Hinweis- und Chat-Mails an diesen Creator sind abgestellt", "Notice and chat emails to this creator are switched off")];
      to = item.talent_id ? `/agentur/nachrichten/${item.talent_id}` : "/agentur/nachrichten";
      break;
    case "calendar_response": {
      const r = str(p.response);
      title = r === "accepted" ? t("Termin zugesagt", "Appointment accepted") : r === "declined" ? t("Termin abgesagt vom Creator", "Appointment declined by the creator") : t("Rückfrage zu einem Termin", "Question about an appointment");
      parts = [talent, str(p.title), terminZeit(p, ctx.timeZone), str(p.note) ? `„${str(p.note)}“` : null];
      to = id ? `/agentur/kalender?termin=${id}` : "/agentur/kalender";
      break;
    }
    case "invoice_missing": {
      const m = str(p.month);
      title = t("Rechnung des Creators fehlt", "Creator invoice missing");
      parts = [talent, brand, str(p.deal_title), month(m), t("angefordert, noch nicht eingereicht", "requested, not yet submitted")];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}${m ? `?monat=${m.slice(0, 7)}` : ""}` : null;
      break;
    }
    case "usage_rights_expiring": {
      const end = str(p.deadline_date);
      const n = num(p.count) ?? 1;
      title = t("Nutzungsrechte laufen aus", "Usage rights are expiring");
      // 0068: das früheste kommende Ende kann von einem Beitrag kommen
      parts = [talent, brand, str(p.title), str(p.label) ? t(`Beitrag ${str(p.label)}`, `Post ${str(p.label)}`) : null, end ? t(`enden am ${date(end)}`, `ending on ${date(end)}`) : null,
        n > 1 ? t(`frühestes von ${n} Enden`, `earliest of ${n} end dates`) : null, t("Verlängerung per Nachtrag anbieten (Kooperation · Details)", "Offer an extension by amendment (Deal · Details)")];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}` : null;
      break;
    }
    // 0072 Anfragen und Angebote
    case "offer_follow_up": {
      const n = num(p.days);
      title = t("Angebot ohne Antwort · nachfassen", "Offer without reply · follow up");
      parts = [brand, str(p.title), str(p.number) ? t(`Angebot ${str(p.number)}`, `Offer ${str(p.number)}`) : null, str(p.sent_on) ? t(`gesendet am ${date(str(p.sent_on)!)}`, `sent on ${date(str(p.sent_on)!)}`) : null,
        n ? t(`seit ${n} ${n === 1 ? t("Tag", "day") : t("Tagen", "days")} keine Antwort`, `no reply for ${n} ${n === 1 ? t("Tag", "day") : t("Tagen", "days")}`) : null];
      const inq = str(p.inquiry_id) ?? id;
      to = inq ? `/agentur/anfragen/${inq}` : "/agentur/anfragen";
      break;
    }
    // 0074 Media-Kit: Vorschlag des Creators zu Kurzbeschreibung und Themen
    case "kit_proposal":
      title = t("Media-Kit: Vorschlag des Creators", "Media kit: creator's suggestion");
      parts = [talent, t("Kurzbeschreibung und Themen prüfen und übernehmen", "Review and adopt short bio and topics")];
      to = item.talent_id ? `/agentur/creator/${item.talent_id}/mediakit` : "/agentur/creator";
      break;
    // 0068 Beiträge
    case "deliverable_submitted": {
      const m = str(p.month);
      title = t("Entwurf eingereicht", "Draft submitted");
      parts = [talent, brand, str(p.deal_title), str(p.label), month(m), num(p.version) && num(p.version)! > 1 ? `Version ${num(p.version)}` : null, t("prüfen und freigeben", "review and approve")];
      // Die Content-Seite wählt den Monat des Beitrags selbst (ein Parameter, damit der Link ohne „&“ auskommt)
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}/content${str(p.deliverable_id) ? `?beitrag=${str(p.deliverable_id)}` : m ? `?monat=${m.slice(0, 7)}` : ""}` : null;
      break;
    }
    case "deliverable_live_reported": {
      const d = str(p.date);
      title = t("Beitrag live · Kennzeichnung prüfen", "Post live · check ad labelling");
      parts = [talent, brand, str(p.deal_title), str(p.label), d ? t(`live seit ${date(d)}`, `live since ${date(d)}`) : null];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}?beitrag=${str(p.deliverable_id) ?? ""}` : null;
      break;
    }
    // 0073 Freigabe durch die Marke (Beträge nur fürs Team) und Kennzahlen
    case "brand_review_approved":
    case "brand_review_changes": {
      const ok = item.type === "brand_review_approved";
      title = ok ? t("Marke hat freigegeben", "Brand approved") : t("Marke wünscht Änderungen", "Brand requests changes");
      const fee = num(p.fee_cents);
      parts = [talent, brand, str(p.deal_title), str(p.label), str(p.name) ? t(`von ${str(p.name)}`, `by ${str(p.name)}`) : null, str(p.comment) ? `„${str(p.comment)}“` : null,
        !ok && num(p.round) ? t(`Korrekturschleife ${num(p.round)}${num(p.included) != null ? ` von ${num(p.included)}` : ""}`, `Revision round ${num(p.round)}${num(p.included) != null ? ` of ${num(p.included)}` : ""}`) : null,
        p.over === true ? t(`zusätzliche Schleife${fee ? t(` · ${money(fee, str(p.currency) ?? "EUR")} laut Vereinbarung`, ` · ${money(fee, str(p.currency) ?? "EUR")} as agreed`) : ""}`, `additional round${fee ? t(` · ${money(fee, str(p.currency) ?? "EUR")} laut Vereinbarung`, ` · ${money(fee, str(p.currency) ?? "EUR")} as agreed`) : ""}`) : null,
        !ok && p.forwarded === false ? t("an den Creator weitergeben", "pass on to the creator") : null];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}/content${str(p.deliverable_id) ? `?beitrag=${str(p.deliverable_id)}` : str(p.month) ? `?monat=${str(p.month)}` : ""}` : null;
      break;
    }
    case "brand_review_forward":
      title = t("Änderungswunsch der Marke weitergeben", "Pass on the brand's change request");
      parts = [talent, brand, str(p.deal_title), str(p.label), str(p.comment) ? `„${str(p.comment)}“` : null, t("Text für den Creator prüfen und weitergeben", "Review the text for the creator and pass it on")];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}/content${str(p.deliverable_id) ? `?beitrag=${str(p.deliverable_id)}` : str(p.month) ? `?monat=${str(p.month)}` : ""}` : null;
      break;
    case "brand_review_expired":
      title = t("Freigabe-Link abgelaufen", "Approval link expired");
      parts = [talent, brand, str(p.deal_title), str(p.label), str(p.recipient) ? t(`an ${str(p.recipient)}`, `to ${str(p.recipient)}`) : null, t("neuen Link senden oder selbst prüfen", "send a new link or review yourself")];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}/content${str(p.deliverable_id) ? `?beitrag=${str(p.deliverable_id)}` : ""}` : null;
      break;
    case "deliverable_metrics_submitted":
      title = t("Kennzahlen hochgeladen", "Metrics uploaded");
      parts = [talent, brand, str(p.deal_title), str(p.label), t("für den Bericht an die Marke", "for the report to the brand")];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}?beitrag=${str(p.deliverable_id) ?? ""}` : null;
      break;
    case "deliverables_overdue": {
      const n = num(p.count) ?? 0;
      title = n === 1 ? t("Beitrag überfällig", "Post overdue") : t("Beiträge überfällig", "Posts overdue");
      const was = [num(p.drafts) ? `${num(p.drafts)} ${num(p.drafts) === 1 ? t("Entwurf", "Draft") : t("Entwürfe", "drafts")}` : null,
        num(p.posts) ? `${num(p.posts)} ${num(p.posts) === 1 ? t("Veröffentlichung", "publication") : t("Veröffentlichungen", "publications")}` : null].filter(Boolean).join(t(" und ", " and "));
      parts = [talent, brand, str(p.deal_title), was || null, beitragsListe(p.items, "de")];
      to = str(p.deal_id) ? `/agentur/kooperationen/${str(p.deal_id)}` : "/agentur/kooperationen/beitraege?filter=ueberfaellig";
      const firstDue = Array.isArray(p.items) ? (p.items as Record<string, unknown>[]).map((x) => str(x?.due)).filter((x): x is string => !!x).sort()[0] ?? null : null;
      due = firstDue;
      break;
    }
    case "collection_handover":
      title = t("An Inkasso übergeben", "Handed over to debt collection");
      parts = [invoiceNo(), brand, amount(p, inv), talent];
      to = toInvoice;
      break;
    case "payout_details_changed":
      title = p.first === true ? t("Bankverbindung hinterlegt", "Bank details added") : t("Bankverbindung geändert", "Bank details changed");
      parts = [talent, str(p.reason) ? `neu: ${str(p.reason)}` : null, t("vor dem nächsten Auszahlungslauf prüfen", "check before the next payout run")];
      to = toTalent;
      break;
    case "self_billing_objected":
      title = t("Creator widerspricht einer Gutschrift", "Creator objects to a self-billing invoice");
      parts = [talent, invoiceNo(), amount(p, inv), str(p.reason) ? t(`Grund: ${str(p.reason)}`, `Reason: ${str(p.reason)}`) : null, t("neu ausstellen oder klären", "reissue or clarify")];
      to = toInvoice;
      break;
    case "support_access_opened":
    case "support_session_started": {
      const until = str(p.date);
      title = item.type === "support_access_opened" ? t("Support-Zugang geöffnet", "Support access opened") : t("Support liest gerade mit", "Support is currently viewing");
      parts = [str(p.reason) ? t(`Grund: ${str(p.reason)}`, `Reason: ${str(p.reason)}`) : null, until ? t(`bis ${date(until)}`, `until ${date(until)}`) : null, t("nur lesend; beenden unter Einstellungen · Team & Rechte", "read-only; end under Settings · Team & permissions")];
      to = "/agentur/einstellungen/team";
      break;
    }
    case "member_mfa_reset":
      title = t("Zweiter Faktor zurückgesetzt", "Second factor reset");
      parts = [str(p.reason), str(p.ticket) ? `Ticket ${str(p.ticket)}` : null, t("vom Plattform-Betrieb nach Identitätsprüfung", "by platform operations after identity verification")];
      to = "/agentur/einstellungen/team";
      break;
    case "tax_status_check_team": {
      const year = num(p.year);
      title = t(`Steuerstatus ${year ?? t("fürs neue Jahr", "for the new year")} klären`, `Clarify tax status ${year ?? t("fürs neue Jahr", "for the new year")}`);
      parts = [talent, vatCh(p) ? t("Creator ohne Portalzugang: MWST-pflichtig ja oder nein erfragen und im Profil mit Datum eintragen", "Creator without portal access: ask whether they are registered for VAT and enter it in the profile with a date")
        : t("Creator ohne Portalzugang: Kleinunternehmer ja oder nein erfragen und im Profil mit Datum eintragen", "Creator without portal access: ask whether they are a small business and enter it in the profile with a date")];
      to = toTalent;
      break;
    }
    case "chef_role_changed":
      title = t("Admin-Rolle geändert", "Admin role changed");
      parts = [str(p.reason)];
      to = "/agentur/einstellungen/team";
      break;
    // 0092: Laufzeitende eines vom Betrieb freigeschalteten Abos (RECHTE-R3-5)
    case "subscription_expiry": {
      const end = str(p.date);
      const until = str(p.deadline_date);
      const stage = str(p.stage);
      // 0180: Stripe-Zahlung über die Nachfrist offen (reason „payment“, date = seit wann offen)
      // K-382 (0282): Test ohne Zahlungsart beendet; der Eintrag nennt den gewählten Tarif mit Preis
      if (stage === "suspended" && str(p.reason) === "trial") {
        const plan = str(p.plan);
        const cents = typeof p.price_cents === "number" ? p.price_cents : null;
        title = t("Dein Test ist beendet, das Portal ist gesperrt", "Your trial has ended, the portal is locked");
        parts = [plan ? t(`Starte ${plan}${cents !== null ? ` für ${money(cents, "EUR")} netto im Monat` : ""}, dann geht es sofort weiter`, `Start ${plan}${cents !== null ? ` for ${money(cents, "EUR")} net per month` : ""} to continue right away`)
          : t("Wähle einen Tarif, dann geht es sofort weiter", "Choose a plan to continue right away"),
          t("deine Daten bleiben erhalten und lassen sich exportieren", "your data is kept and can be exported")];
        to = "/agentur/einstellungen/abo";
        break;
      }
      if (stage === "suspended" && str(p.reason) === "payment") {
        title = t("Agentur wegen offener Zahlung nur lesbar", "Agency read-only because a payment is outstanding");
        parts = [end ? t(`Zahlung offen seit ${date(end)}`, `payment outstanding since ${date(end)}`) : null,
          t("Zahlungsart unter Abo & Tarif · Rechnungen und Zahlungsart verwalten aktualisieren; mit der Zahlung ist alles wieder offen", "update the payment method under Plan · Manage invoices and payment method; once paid, everything is unlocked again")];
        to = "/agentur/einstellungen/abo";
        break;
      }
      // 0425: Laufzeit ohne Verlängerung abgelaufen = Vertrag beendet, Portal gesperrt
      title = stage === "suspended" ? t("Abo abgelaufen, Portal gesperrt", "Subscription expired, portal locked")
        : stage === "expired" ? t("Abo-Laufzeit abgelaufen", "Subscription term expired") : t("Abo-Laufzeit endet bald", "Subscription term ending soon");
      parts = [end ? (stage === "soon" ? t(`endet am ${date(end)}`, `ends on ${date(end)}`) : t(`abgelaufen am ${date(end)}`, `expired on ${date(end)}`)) : null,
        stage === "expired" && until ? t(`ohne Verlängerung ab ${date(until)} gesperrt, Export bleibt`, `locked from ${date(until)} without renewal, export remains`) : null,
        t("Verlängerung beim Plattform-Betrieb oder unter Abo & Tarif anfragen", "request a renewal from platform operations or under Plan")];
      to = "/agentur/einstellungen/abo";
      break;
    }
    // 0132: verzögerte Zahlung (SEPA-Lastschrift) beim Abschluss gescheitert; die Agentur bleibt, wie sie war.
    // 0157 (source „invoice“): Rechnung des laufenden Stripe-Abos nicht bezahlt (invoice.payment_failed)
    case "subscription_payment_failed": {
      title = t("Zahlung für das Abo gescheitert", "Subscription payment failed");
      if (str(p.source) === "action_required") {
        // 0180: invoice.payment_action_required (3-D Secure): die Bank verlangt eine Bestätigung
        title = t("Zahlung für das Abo bestätigen", "Confirm the subscription payment");
        const cents = num(p.amount_cents);
        parts = [cents !== null ? money(cents, str(p.currency) ?? "EUR") : null,
          t("deine Bank verlangt eine Bestätigung (3-D Secure)", "your bank requires a confirmation (3-D Secure)"),
          t("unter Abo & Tarif · Rechnungen und Zahlungsart verwalten bestätigen bzw. über den Link in der Zahlungs-Mail", "confirm under Plan · Manage invoices and payment method or via the link in the payment email")];
      } else if (str(p.source) === "overdue") {
        // 0590: Abo auf Rechnung, Fälligkeit überschritten (invoice.overdue bzw. Abo auf past_due)
        title = t("Rechnung für das Abo überfällig", "Subscription invoice overdue");
        const cents = num(p.amount_cents);
        const due = str(p.due_date);
        parts = [str(p.number) ? t(`Rechnung ${str(p.number)}`, `invoice ${str(p.number)}`) : null, cents !== null ? money(cents, str(p.currency) ?? "EUR") : null,
          due ? t(`fällig seit ${date(due)}`, `due since ${date(due)}`) : null,
          t("bitte mit der Rechnungsnummer als Verwendungszweck überweisen; die Bankverbindung steht unter Abo & Tarif", "please transfer with the invoice number as the payment reference; the bank details are under Plan"),
          t("bleibt die Zahlung länger als 14 Tage offen, ist die Agentur danach nur noch lesbar", "if the payment stays outstanding for more than 14 days, the agency becomes read-only")];
      } else if (str(p.source) === "invoice") {
        const cents = num(p.amount_cents);
        const next = str(p.next_attempt_date);
        parts = [cents !== null ? money(cents, str(p.currency) ?? "EUR") : null,
          p.final === true || !next ? t("kein weiterer Versuch", "no further attempt")
            : t(`nächster Versuch am ${date(next)}`, `next attempt on ${date(next)}`),
          t("Zahlungsart unter Abo & Tarif · Rechnungen und Zahlungsart verwalten aktualisieren", "update the payment method under Plan · Manage invoices and payment method"),
          // 0181 (Zusatz 38, AGB „Preise und Zahlung“): Ankündigung der Beschränkung auf Lesen
          t("bleibt die Zahlung länger als 14 Tage offen, ist die Agentur danach nur noch lesbar", "if the payment stays outstanding for more than 14 days, the agency becomes read-only")];
      } else {
        parts = [t("die Lastschrift wurde nicht eingelöst; Tarif und Stand der Agentur bleiben unverändert",
          "the direct debit was not honoured; the agency's plan and status stay unchanged"),
          t("unter Abo & Tarif erneut abschließen, am besten mit Karte", "subscribe again under Plan, preferably by card")];
      }
      to = "/agentur/einstellungen/abo";
      break;
    }
    // 0134: Speicher des Tarifs zu 80, 90 oder 100 % belegt (je Stufe einmal, nur Admins)
    case "storage_quota": {
      const stage = num(p.stage) ?? 80;
      const pct = num(p.percent);
      const gb = num(p.limit_gb);
      title = stage >= 100 ? t("Speicher voll", "Storage full") : t(`Speicher zu ${stage} % belegt`, `Storage ${stage} % used`);
      parts = [pct !== null && gb !== null ? t(`${pct} % von ${gb} GB`, `${pct} % of ${gb} GB`) : null,
        stage >= 100 ? t("neue Dateien werden abgelehnt; Rechnungen, Belege und Verträge weiter abgelegt", "new files are rejected; invoices, receipts and contracts are still stored")
          : t("über der Grenze werden neue Dateien abgelehnt", "above the limit new files are rejected"),
        t("Dateien aufräumen oder unter Abo & Tarif einen größeren Tarif wählen", "clean up files or choose a larger plan under Plan")];
      to = "/agentur/einstellungen/abo";
      break;
    }
    case "talent_joined":
      title = t("Neuer Creator", "New creator");
      parts = [talent, t("Stammdaten vervollständigen", "Complete master data")];
      to = toTalent;
      break;
    case "talent_left_portal": {
      // 0086: offene Posten zum Zeitpunkt des Löschantrags; Auszahlungen und Verträge laufen ohne Portal weiter
      const n = (k: string) => Number(p[k] ?? 0) || 0;
      const pay = n("payouts"), sign = n("contracts_to_sign"), inv = n("agency_invoices");
      title = t("Creator hat den Portalzugang beendet", "Creator ended portal access");
      parts = [talent,
        pay === 1 ? t("1 offene Auszahlung", "1 open payout") : pay > 1 ? t(`${pay} offene Auszahlungen`, `${pay} open payouts`) : null,
        sign === 1 ? t("1 Vertrag zur Unterschrift", "1 contract to sign") : sign > 1 ? t(`${sign} Verträge zur Unterschrift`, `${sign} contracts to sign`) : null,
        inv === 1 ? t("1 offene Rechnung an den Creator", "1 open invoice to the creator") : inv > 1 ? t(`${inv} offene Rechnungen an den Creator`, `${inv} open invoices to the creator`) : null,
        t("Kontakt künftig per Mail", "Contact by email from now on")];
      to = toTalent;
      break;
    }
    // ── an den Creator ──
    case "invoice_request": {
      const m = str(p.month);
      title = p.again === true ? t("Rechnung erneut angefordert", "Invoice requested again") : t("Bitte Rechnung stellen", "Please submit your invoice");
      parts = [brand, str(p.deal_title), month(m), str(p.note) ? `${t("Hinweis", "Note")}: ${str(p.note)}` : null];
      to = str(p.deal_id) && m ? `/creator/rechnungen/einreichen?kooperation=${str(p.deal_id)}&monat=${m.slice(0, 7)}` : "/creator/rechnungen/einreichen";
      break;
    }
    case "invoice_revision":
    case "invoice_rejected":
      title = item.type === "invoice_rejected" ? t("Rechnung abgelehnt", "Invoice rejected") : t("Kurze Korrektur nötig", "Small correction needed");
      parts = [invoiceNo(), str(p.deal_title), str(p.reason)];
      to = id ? `/creator/rechnungen/einreichen?rechnung=${id}` : "/creator/rechnungen";
      break;
    // 0087: die Agentur hat eine Ablehnung bzw. Rückgabe zurückgenommen (PARITAET-16)
    case "invoice_rejection_reverted":
      title = t("Rückmeldung zurückgenommen", "Feedback withdrawn");
      parts = [invoiceNo(), str(p.deal_title), t("Die Rechnung ist wieder in Prüfung; du musst nichts ändern", "The invoice is back in review; you do not need to change anything")];
      to = toInvoice;
      break;
    case "payment_check": {
      const mode = str(p.mode);
      title = t("Ist das Geld angekommen?", "Has the payment arrived?");
      due = str(p.due_date);
      parts = [invoiceNo(), amount(p, inv), str(p.deal_title), due ? t(`fällig am ${fmtDate(due)}`, `due on ${fmtDate(due)}`) : null,
        mode === "report_rejected" ? t(`Deine Agentur sieht noch keinen Eingang${str(p.note) ? `: ${str(p.note)}` : ""}`, `Your agency cannot see the payment yet${str(p.note) ? `: ${str(p.note)}` : ""}`)
          : mode === "preconfirmed" ? t("Deine Agentur hat die Auszahlung bestätigt", "Your agency has confirmed the payout") : null];
      to = toInvoice;
      break;
    }
    case "contract_to_sign":
      title = t("Vertrag zur Unterschrift", "Contract to sign");
      parts = [str(p.title)];
      to = toContract;
      break;
    // 0458 Prüf-Chat CP-022 (02.11.2026): Freigabe der eigenen Rechnung mit voraussichtlicher Auszahlung
    case "invoice_approved": {
      const due = str(p.due_date);
      title = t("Rechnung freigegeben", "Invoice approved");
      parts = [invoiceNo(), amount(p, inv), str(p.deal_title), due ? t(`Auszahlung voraussichtlich bis ${fmtDate(due)}`, `payout expected by ${fmtDate(due)}`) : null];
      to = toInvoice;
      break;
    }
    case "payment_confirmed":
      title = t("Zahlung bestätigt", "Payment confirmed");
      parts = [invoiceNo(), amount(p, inv), str(p.deal_title)];
      to = toInvoice;
      break;
    case "payment_reset":
      title = t("Zahlung zurückgesetzt", "Payment reset");
      parts = [invoiceNo(), t("Deine Agentur hat die Zahlungsbestätigung zurückgenommen", "Your agency has withdrawn the payment confirmation")];
      to = toInvoice;
      break;
    case "invoice_cancelled":
      title = t("Rechnung storniert", "Invoice cancelled");
      parts = [invoiceNo(), str(p.reason)];
      to = toInvoice;
      break;
    case "agency_invoice_issued": {
      // Rechnung der Agentur an den Creator (Befund 5): der Creator zahlt an die Agentur
      const d = str(p.due_date);
      title = t("Rechnung deiner Agentur", "Invoice from your agency");
      parts = [invoiceNo(), amount(p, inv), str(p.deal_title), d ? t(`fällig am ${fmtDate(d)}`, `due on ${fmtDate(d)}`) : null,
        t("Zahlung an deine Agentur", "Payment to your agency")];
      due = d;
      to = toInvoice;
      break;
    }
    case "self_billing_issued":
      title = t("Gutschrift deiner Agentur", "Self-billing invoice from your agency");
      parts = [invoiceNo(), amount(p, inv), str(p.deal_title), t("bitte prüfen; stimmt etwas nicht, kannst du widersprechen", "please check; if something is wrong, you can object")];
      to = toInvoice;
      break;
    case "self_billing_cancelled": {
      // 0345: Stornobeleg zur Gutschrift (eigene Nummer); die Meldung führt auf den Beleg und nennt die stornierte Gutschrift
      const n = str(p.number);
      const c = str(p.credit_number);
      title = t("Gutschrift storniert", "Self-billing invoice cancelled");
      parts = [n ? t(`Gutschrift ${n}`, `Self-billing invoice ${n}`) : null, c ? t(`Stornobeleg ${c}`, `Cancellation document ${c}`) : null, str(p.reason)];
      to = toInvoice;
      break;
    }
    case "bank_details_changed":
      title = t("Deine Bankverbindung wurde geändert", "Your bank details were changed");
      parts = [str(p.reason) ? `${t("neu", "new")}: ${str(p.reason)}` : null,
        t("Warst du das nicht, melde dich sofort bei deiner Agentur", "If this was not you, contact your agency immediately")];
      to = "/creator/profil";
      break;
    case "org_readonly_notice": {
      const end = str(p.deadline_date);
      // 0425: gilt für beendete (gesperrt) und nur lesbare Agenturen; der eigene Download unter Konto geht in beiden Fällen
      title = t("Agentur nutzt CreatorOffice nicht aktiv", "Agency is not actively using CreatorOffice");
      parts = [end ? t(`deine Daten bis ${fmtDate(end)} unter Konto herunterladen`, `download your data under Account by ${fmtDate(end)}`)
        : t("deine Daten kannst du unter Konto herunterladen", "you can download your data under Account")];
      to = "/creator/konto";
      break;
    }
    case "contract_resumed":
      title = t("Vertrag läuft weiter", "Contract resumed");
      parts = [str(p.title), t("fehlt deine Unterschrift, kannst du jetzt unterschreiben", "if your signature is missing, you can sign now")];
      to = toContract;
      break;
    // 0085: neuer bzw. gestrichener Beitrag in einer laufenden Kooperation (AUTOMATIK-18)
    case "deliverable_added": {
      const end = str(p.deadline_date);
      const n = num(p.count) ?? 1;
      title = n > 1 ? t(`${n} neue Beiträge`, `${n} new posts`) : t("Neuer Beitrag", "New post");
      parts = [brand, str(p.deal_title), str(p.reason), end ? t(`fällig ${fmtDate(end)}`, `due ${fmtDate(end)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      break;
    }
    // 0458 Prüf-Chat CP-014 (02.11.2026): neue Kooperation, sobald sie für den Creator sichtbar ist
    case "deal_new":
      title = t("Neue Kooperation", "New collaboration");
      parts = [brand, str(p.deal_title), t("Briefing, Beiträge und deinen Anteil findest du in der Kooperation", "You will find the briefing, content items and your share in the collaboration")];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}` : "/creator/deals";
      break;
    case "deliverable_cancelled": {
      const prev = str(p.previous_deadline);
      title = t("Beitrag entfällt", "Post cancelled");
      parts = [brand, str(p.deal_title), str(p.reason), prev ? t(`bisher fällig ${fmtDate(prev)}`, `previously due ${fmtDate(prev)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      break;
    }
    case "deliverable_deadline_changed": {
      const end = str(p.deadline_date);
      const prev = str(p.previous_deadline);
      title = end ? t("Termin eines Beitrags geändert", "Content date changed") : t("Termin eines Beitrags entfällt", "Content date removed");
      parts = [brand, str(p.deal_title), str(p.reason), end ? t(`neu: ${fmtDate(end)}`, `new: ${fmtDate(end)}`) : null,
        prev ? t(`bisher ${fmtDate(prev)}`, `previously ${fmtDate(prev)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      break;
    }
    case "tax_status_check": {
      const year = num(p.year);
      title = t(`Steuerstatus für ${year ?? "das neue Jahr"} bestätigen`, `Confirm your tax status for ${year ?? "the new year"}`);
      // Schweiz und Liechtenstein: MWST-Begriffe (vat_country, 0087)
      parts = [vatCh(p) ? t("Bleibst du nicht mehrwertsteuerpflichtig? Wenn nicht, trag im Profil ein, ab wann du MWST ausweist.",
        "Will you remain not registered for VAT? If not, enter in your profile from which date you charge VAT.")
        : t("Bleibst du Kleinunternehmer? Wenn nicht, trag im Profil ein, ab wann du Umsatzsteuer ausweist.",
          "Will you remain a small business? If not, enter in your profile from which date you charge VAT.")];
      to = "/creator/profil";
      break;
    }
    // 0079: Creator hat den Steuerstatus rückwirkend umgestellt; schon ausgestellte Rechnungen passen nicht mehr (CREATOR-5)
    case "tax_status_retro": {
      const from = str(p.valid_from);
      const numbers = Array.isArray(p.numbers) ? (p.numbers as unknown[]).filter((x): x is string => typeof x === "string") : [];
      const n = num(p.count) ?? numbers.length;
      title = t("Steuerstatus rückwirkend geändert", "Tax status changed retroactively");
      parts = [talent, [smallBusinessWord(vatCh(p) ? "CH" : null, p.small_business === true), from ? t(`ab ${date(from)}`, `from ${date(from)}`) : null].filter(Boolean).join(" "),
        t(`${n} ${n === 1 ? t("Rechnung passt", "invoice fits") : t("Rechnungen passen", "invoices fit")} nicht mehr${numbers.length ? `: ${numbers.join(", ")}` : ""}`, `${n} ${n === 1 ? t("Rechnung passt", "invoice fits") : t("Rechnungen passen", "invoices fit")} no longer${numbers.length ? `: ${numbers.join(", ")}` : ""}`), t("Korrektur mit dem Creator klären", "Clarify the correction with the creator")];
      to = item.talent_id ? `/agentur/creator/${item.talent_id}` : "/agentur/creator";
      break;
    }
    case "org_tax_status_check": {
      const year = num(p.year);
      title = t(`Steuerstatus der Agentur für ${year ?? t("das neue Jahr", "the new year")} bestätigen`, `Confirm the agency's tax status for ${year ?? t("das neue Jahr", "the new year")}`);
      parts = [vatCh(p) ? t("Bleibt die Agentur nicht mehrwertsteuerpflichtig? Wenn nicht, unter Einstellungen · Firma eintragen, ab wann MWST gilt.", "Does the agency remain not registered for VAT? If not, enter under Settings · Company from when VAT applies.")
        : t("Bleibt die Agentur Kleinunternehmer? Wenn nicht, unter Einstellungen · Firma eintragen, ab wann Umsatzsteuer gilt.", "Does the agency remain a small business? If not, enter under Settings · Company from when VAT applies.")];
      to = "/agentur/einstellungen/firma";
      break;
    }
    case "payout_revoked":
      title = t("Auszahlung zurückgenommen", "Payout withdrawn");
      parts = [invoiceNo(), t("Die Bestätigung der Auszahlung gilt nicht mehr", "The payout confirmation no longer applies")];
      to = toInvoice;
      break;
    case "contract_completed":
      title = p.external === true ? t("Unterschriebener Vertrag hinterlegt", "Signed contract filed") : t("Vertrag vollständig unterschrieben", "Contract fully signed");
      parts = [str(p.title)];
      to = toContract;
      break;
    // ── Verträge (0040): angehalten, aufgehoben, gekündigt an den Creator; Aufgaben an das Team ──
    case "contract_stopped":
    case "contract_voided":
    case "contract_terminated":
    case "contract_deal_cancelled":
    case "contract_link_expired":
    case "contract_deadline": {
      const k = item.type;
      title = k === "contract_stopped" ? t("Vertrag angehalten", "Contract paused") : k === "contract_voided" ? t("Vertrag aufgehoben", "Contract voided")
        : k === "contract_terminated" ? t("Vertrag gekündigt", "Contract terminated")
          : k === "contract_deal_cancelled" ? (str(p.mode) === "archived" ? t("Kooperation archiviert: Vertrag prüfen", "Deal archived: check contract") : t("Kooperation storniert: Vertrag prüfen", "Deal cancelled: check contract"))
            : k === "contract_link_expired" ? t("Vertragslink abgelaufen", "Contract link expired") : t("Kündigungsfrist beachten", "Mind the notice period");
      if (k === "contract_deadline") due = str(p.deadline);
      parts = [str(p.title), A ? talent : null, k === "contract_deal_cancelled" ? str(p.deal_title) : null,
        k === "contract_terminated" && str(p.date) ? t(`zum ${fmtDate(str(p.date)!)}`, `effective ${fmtDate(str(p.date)!)}`) : null,
        k === "contract_deadline" && due ? t(`kündigen bis ${date(due)}`, `terminate by ${date(due)}`) : null, k === "contract_voided" ? str(p.reason) : null];
      to = toContract;
      break;
    }
    case "calendar_event":
    case "calendar_event_cancelled": {
      const day = str(p.date);
      title = item.type === "calendar_event_cancelled" ? t("Termin abgesagt", "Date cancelled") : p.changed === true ? t("Termin geändert", "Date changed") : t("Neuer Termin", "New date");
      // Uhrzeit in der Zone der betrachtenden Person (Befund 15)
      const prev = p.changed === true && str(p.previous_date) ? terminZeit({ date: p.previous_date, start_time: p.previous_start_time, time_zone: p.previous_time_zone ?? p.time_zone }, ctx.timeZone, lang) : null;
      // K-559 c: Ort und Hinweis auf den Link (der Link selbst steht im Termin, die Meldung öffnet ihn)
      parts = [str(p.title), terminZeit(p, ctx.timeZone, lang), prev ? t(`bisher ${prev}`, `previously ${prev}`) : null,
        item.type === "calendar_event" ? str(p.location) : null, item.type === "calendar_event" && str(p.meeting_url) ? t("mit Link für den Call", "with call link") : null];
      to = item.type === "calendar_event" && id ? `/creator/kalender?termin=${id}${day ? `&datum=${day}` : ""}` : day ? `/creator/kalender?datum=${day}` : "/creator/kalender";
      break;
    }
    case "invoice_in_collection":
      title = t("Rechnung beim Inkasso", "Invoice with collection agency");
      parts = [invoiceNo(), brand, amount(p, inv), t("Deine Agentur hat die Forderung übergeben", "Your agency has handed over the claim")];
      to = toInvoice;
      break;
    // 0094: Übergabe zurückgenommen (AUTOMATIK-R3-2)
    case "invoice_collection_withdrawn":
      title = t("Nicht mehr beim Inkasso", "No longer with debt collection");
      parts = [invoiceNo(), brand, amount(p, inv), t("Deine Agentur hat die Übergabe zurückgenommen; du musst nichts tun", "Your agency has withdrawn the handover; you do not need to do anything")];
      to = toInvoice;
      break;
    case "content_deadline_soon": {
      const end = str(p.deadline_date);
      title = t("Content-Deadline naht", "Content deadline approaching");
      parts = [brand, str(p.deal_title), end ? t(`Abgabe am ${fmtDate(end)}`, `due on ${fmtDate(end)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}` : "/creator/deals";
      break;
    }
    // 0073: Entscheidung der Marke und Kennzahlen (nur, was der Creator sehen darf)
    case "brand_approved":
      title = t("Marke hat freigegeben", "Brand approved");
      parts = [brand, str(p.deal_title), str(p.label), t("du kannst posten", "you can post"), str(p.reason) ? t(`Hinweis: ${str(p.reason)}`, `Note: ${str(p.reason)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      break;
    case "brand_changes":
      title = t("Änderungswunsch der Marke", "Brand requested changes");
      parts = [brand, str(p.deal_title), str(p.label), str(p.reason) ? `„${str(p.reason)}“` : t("Details folgen von deiner Agentur", "details to follow from your agency")];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      break;
    // 0155: Korrektur der Agentur (Glocke und Mail immer), mit Kommentaren an der Datei
    case "content_changes":
      title = t("Korrektur am Entwurf", "Changes to your draft");
      parts = [brand, str(p.deal_title), str(p.label), str(p.reason) ? `„${str(p.reason)}“` : null,
        Number(p.task_count) > 0 ? t(`${Number(p.task_count)} Kommentare an der Datei`, `${Number(p.task_count)} comments on the file`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      break;
    case "deliverable_metrics_due": {
      const end = str(p.deadline_date);
      title = t("Kennzahlen hochladen", "Upload metrics");
      parts = [brand, str(p.deal_title), str(p.label), t("Screenshots und Werte für den Bericht", "screenshots and numbers for the report"), end ? t(`bis ${fmtDate(end)}`, `by ${fmtDate(end)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      due = end;
      break;
    }
    case "deliverable_draft_due": {
      const end = str(p.deadline_date);
      title = t("Entwurf morgen fällig", "Draft due tomorrow");
      parts = [brand, str(p.deal_title), beitragsListe(p.items, lang), end ? t(`bis ${fmtDate(end)}`, `by ${fmtDate(end)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}` : "/creator/deals";
      break;
    }
    // 0095 (PRAXIS-R3-4): Erinnerungen an den Creator zum Posttermin und zu verstrichenen Terminen
    case "deliverable_publish_due": {
      const end = str(p.deadline_date);
      title = t("Veröffentlichung morgen", "Posting due tomorrow");
      parts = [brand, str(p.deal_title), beitragsListe(p.items, lang), end ? t(`am ${fmtDate(end)}`, `on ${fmtDate(end)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      due = end;
      break;
    }
    case "deliverable_overdue_talent": {
      const n = num(p.count) ?? 0;
      title = n === 1 ? t("Beitrag überfällig", "Content overdue") : t("Beiträge überfällig", "Content overdue");
      parts = [brand, str(p.deal_title), beitragsListe(p.items, lang)];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}#beitraege` : "/creator/deals";
      break;
    }
    case "content_deadline_changed": {
      const end = str(p.deadline_date);
      const prev = str(p.previous_deadline);
      title = end ? t("Deadline geändert", "Deadline changed") : t("Deadline entfällt", "Deadline removed");
      parts = [brand, str(p.deal_title), month(str(p.month)), end ? t(`neu: ${fmtDate(end)}`, `new: ${fmtDate(end)}`) : null,
        prev ? t(`bisher ${fmtDate(prev)}`, `previously ${fmtDate(prev)}`) : null];
      to = str(p.deal_id) ? `/creator/deals/${str(p.deal_id)}${str(p.month) ? `?monat=${str(p.month)!.slice(0, 7)}` : ""}` : "/creator/deals";
      break;
    }
    default:
      parts = [talent];
  }

  const pills: Pill[] = [];
  if (item.state === "resolved" && item.resolve_reason) parts.push(`${t("Geschlossen", "Closed")}: ${item.resolve_reason}`);
  if (item.state === "resolved") pills.push(pill(t("Erledigt", "Done"), "success"));
  else if (item.state === "dismissed") pills.push(pill(t("Ausgeblendet", "Hidden"), "neutral"));
  else {
    if (item.unread) pills.push(pill(t("Neu", "New"), "info"));
    if (item.is_task) {
      const late = overdueDays(due, ctx.today);
      pills.push(late > 0 ? pill(lang === "en" ? `${late} ${late === 1 ? "day" : "days"}` : `${late} ${late === 1 ? "Tag" : "Tage"}`, "error") : pill(t("Aufgabe", "Task"), "warning"));
      if (A && item.assigned_to) pills.push(pill(t(`Übernommen: ${item.assigned_name ?? "Team"}`, `Taken over: ${item.assigned_name ?? "Team"}`), "neutral"));
    }
  }
  const detail = parts.filter((x): x is string => !!x).join(" · ");
  return {
    item,
    title,
    detail,
    to,
    category: TYPE_CATEGORY[item.type] ?? null,
    pills,
    dismissible: item.state === "open" && !item.is_task,
    answerInvoice: item.state === "open" && item.type === "payment_check" && id ? id : null,
    search: `${title} ${detail} ${talent ?? ""}`.toLowerCase(),
  };
}

export type StateFilter = "offen" | "ungelesen" | "erledigt";
export function matchesState(item: Pick<NotificationItem, "state" | "unread">, f: StateFilter): boolean {
  if (f === "erledigt") return item.state !== "open";
  if (f === "ungelesen") return item.state === "open" && item.unread;
  return item.state === "open";
}

/** Offene Aufgaben nach Dringlichkeit (Typ-Reihenfolge des Katalogs), dann neueste zuerst. */
export function sortTasks<T extends { item: Pick<NotificationItem, "sort_order" | "created_at"> }>(views: readonly T[]): T[] {
  return [...views].sort((a, b) => a.item.sort_order - b.item.sort_order || b.item.created_at.localeCompare(a.item.created_at));
}

export interface DayGroup<T> {
  day: string;
  label: string;
  items: T[];
}
/** Nach Kalendertag gruppiert, neueste Tage und Einträge zuerst (Paket: Abschnitt „Heute“). */
export function groupByDay<T>(items: readonly T[], at: (x: T) => string, today: string, timeZone: string, lang: Lang = currentLang()): DayGroup<T>[] {
  const sorted = [...items].sort((a, b) => at(b).localeCompare(at(a)));
  const groups: DayGroup<T>[] = [];
  for (const x of sorted) {
    const day = localDay(at(x), timeZone);
    let g = groups.at(-1);
    if (!g || g.day !== day) {
      g = { day, label: dayLabel(day, today, lang), items: [] };
      groups.push(g);
    }
    g.items.push(x);
  }
  return groups;
}

/** Zähler für den Kopf der Seiten und die Glocke im Seitenrahmen. */
export interface UnreadCount {
  /** ungelesene Meldungen (Aufgaben und Hinweise) */
  notifications: number;
  /** offene Aufgaben (gelesen oder nicht) */
  tasks: number;
  /** ungelesene Nachrichten der Gegenseite */
  messages: number;
  /**
   * Zahl an der Glocke: offene Aufgaben (auch gelesene, sie sind nicht erledigt), ungelesene Hinweise und ungelesene
   * Nachrichten (Befund 16: nach „Alle als gelesen“ stand sonst 0, obwohl Aufgaben offen waren).
   */
  total: number;
}
export function unreadSummary(threads: readonly { unread: number }[], notifications: readonly { unread: boolean; is_task: boolean }[]): UnreadCount {
  const messages = threads.reduce((s, t) => s + (Number(t.unread) || 0), 0);
  const unread = notifications.filter((n) => n.unread).length;
  const tasks = notifications.filter((n) => n.is_task).length;
  const unreadInfos = notifications.filter((n) => n.unread && !n.is_task).length;
  return { notifications: unread, tasks, messages, total: tasks + unreadInfos + messages };
}
export const EMPTY_COUNT: UnreadCount = { notifications: 0, tasks: 0, messages: 0, total: 0 };

/** „1 ungelesene Nachricht“ / „3 ungelesene Nachrichten“ */
export function countLabel(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}
